import React, { useState, useEffect, useRef } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { supabase } from './supabase';
import './App.css';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const BACKEND = 'https://tambo-production-826b.up.railway.app';

// Resolves the Supabase token at call time so it works immediately after sign-in.
// Attaches Authorization: Bearer <token>. Refreshes and retries once on 401.
// Does NOT call signOut — callers handle session cleanup so there is no lock
// contention between a pending signOut and a subsequent getSession call.
async function clientFetch(url, options) {
  var sessionResult = await supabase.auth.getSession();
  var tok = sessionResult.data && sessionResult.data.session && sessionResult.data.session.access_token;
  if (!tok) {
    throw new Error('NOT_AUTHENTICATED');
  }
  function makeReq(t) {
    var merged = Object.assign({}, options, {
      headers: Object.assign({}, options && options.headers, { 'Authorization': 'Bearer ' + t }),
    });
    return fetch(url, merged);
  }
  var res = await makeReq(tok);
  if (res.status === 401) {
    var refreshResult = await supabase.auth.refreshSession();
    var newTok = refreshResult.data && refreshResult.data.session && refreshResult.data.session.access_token;
    if (!newTok || refreshResult.error) {
      throw new Error('NOT_AUTHENTICATED');
    }
    res = await makeReq(newTok);
    if (res.status === 401) {
      throw new Error('NOT_AUTHENTICATED');
    }
  }
  return res;
}

function RollOffDumpsterSVG({ size = 72 }) {
  const h = Math.round(size * (220 / 500));
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 220" width={size} height={h}>
      <rect x="0" y="20" width="500" height="24" rx="3" fill="#145c37"/>
      <rect x="0" y="40" width="500" height="160" rx="4" fill="#1A7A4A"/>
      <rect x="72" y="42" width="8" height="156" rx="2" fill="#145c37"/>
      <rect x="144" y="42" width="8" height="156" rx="2" fill="#145c37"/>
      <rect x="216" y="42" width="8" height="156" rx="2" fill="#145c37"/>
      <rect x="288" y="42" width="8" height="156" rx="2" fill="#145c37"/>
      <rect x="360" y="42" width="8" height="156" rx="2" fill="#145c37"/>
      <rect x="432" y="42" width="8" height="156" rx="2" fill="#145c37"/>
      <rect x="10" y="55" width="28" height="40" rx="3" fill="#145c37"/>
      <rect x="14" y="60" width="20" height="30" rx="2" fill="#1A7A4A"/>
      <rect x="462" y="55" width="28" height="40" rx="3" fill="#145c37"/>
      <rect x="466" y="60" width="20" height="30" rx="2" fill="#1A7A4A"/>
      <rect x="20" y="200" width="120" height="14" rx="4" fill="#145c37"/>
      <rect x="360" y="200" width="120" height="14" rx="4" fill="#145c37"/>
      <rect x="0" y="40" width="500" height="160" rx="4" fill="none" stroke="#0d3d22" strokeWidth="3"/>
      <rect x="0" y="20" width="500" height="24" rx="3" fill="none" stroke="#0d3d22" strokeWidth="2"/>
    </svg>
  );
}

function getPrice(item, dur) {
  if (dur === '3day') return item.p3 || item.price_3day;
  if (dur === '5day') return item.p5 || item.price_5day;
  return item.p7 || item.price_7day;
}

const TAX_RATE = 0.0825;
function calcTotals(price, envFee) {
  const fee = envFee || 0;
  const subtotal = price + fee;
  const tax = Math.round(subtotal * TAX_RATE * 100) / 100;
  const total = Math.round((subtotal + tax) * 100) / 100;
  return { subtotal, tax, total };
}

function formatTimeWindow(start, end) {
  if (!start || !end) return '';
  const fmt = function(t) {
    const h = parseInt(t.split(':')[0]);
    return (h % 12 || 12) + (h >= 12 ? 'pm' : 'am');
  };
  return fmt(start) + '–' + fmt(end);
}

const WASTE_TYPES = [
  'General household junk','Construction debris','Renovation materials',
  'Yard waste / landscaping','Roofing materials','Concrete / dirt',
  'Heavy debris','Furniture / appliances','Commercial waste','Mixed debris',
];
const CONCRETE_WASTE_TYPES = ['Concrete / dirt', 'Heavy debris'];

function GuestInfoForm({ onNext, onCancel, total, item, duration, initialData }) {
  const [name, setName] = useState(initialData?.name || '');
  const [email, setEmail] = useState(initialData?.email || '');
  const [phone, setPhone] = useState(initialData?.phone || '');
  const [company, setCompany] = useState(initialData?.company || '');
  const [streetAddress, setStreetAddress] = useState(initialData?.streetAddress || '');
  const [city, setCity] = useState(initialData?.city || '');
  const [stateAbbr, setStateAbbr] = useState(initialData?.stateAbbr || '');
  const [zip, setZip] = useState(initialData?.zip || '');
  const [wasteType, setWasteType] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [billingSameAsService, setBillingSameAsService] = useState(true);
  const [billingStreet, setBillingStreet] = useState('');
  const [billingCity, setBillingCity] = useState('');
  const [billingState, setBillingState] = useState('');
  const [billingZip, setBillingZip] = useState('');
  const [error, setError] = useState('');

  const is7yd = item.size === '7yd';

  const handleNext = function() {
    if (!name || !email || !phone || !streetAddress || !city || !stateAbbr || !zip || !wasteType) {
      setError('Please fill in all required fields');
      return;
    }
    if (!email.includes('@')) { setError('Please enter a valid email'); return; }
    if (is7yd && !CONCRETE_WASTE_TYPES.includes(wasteType)) {
      setError('7-yard dumpsters are for concrete and heavy debris only. Please select a concrete-compatible waste type.');
      return;
    }
    if (!billingSameAsService && (!billingStreet || !billingCity || !billingState || !billingZip)) {
      setError('Please fill in all billing address fields');
      return;
    }
    const fullAddress = streetAddress + ', ' + city + ', ' + stateAbbr + ' ' + zip;
    const billingFull = billingSameAsService
      ? fullAddress
      : billingStreet + ', ' + billingCity + ', ' + billingState + ' ' + billingZip;
    onNext({
      name, email, phone, company,
      serviceAddress: fullAddress,
      wasteType, deliveryZip: zip, smsConsent, smsConsentAt: smsConsent ? new Date().toISOString() : null,
      billingSameAsService,
      billingAddress: billingFull,
      billingStreet: billingSameAsService ? streetAddress : billingStreet,
      billingCity: billingSameAsService ? city : billingCity,
      billingState: billingSameAsService ? stateAbbr : billingState,
      billingZip: billingSameAsService ? zip : billingZip,
    });
  };

  const fieldStyle = {width:'100%',padding:'11px 14px',border:'1.5px solid #E0E0DA',borderRadius:'10px',fontSize:'14px',outline:'none',background:'white'};
  const labelStyle = {fontSize:'11px',fontWeight:'700',color:'#6B6B6B',textTransform:'uppercase',letterSpacing:'.5px',display:'block',marginBottom:'5px'};

  return (
    <div style={{minHeight:'100vh',background:'#EDEDEA'}}>
      <div style={{background:'#0D0D0D',padding:'16px 24px'}}>
        <div onClick={function(){window.location.href='/';}} style={{fontFamily:'sans-serif',fontSize:'22px',fontWeight:'800',color:'white',cursor:'pointer'}}>
          Tambo
        </div>
      </div>
      <div style={{background:'#1A7A4A',padding:'16px 24px',color:'white'}}>
        <button onClick={onCancel} style={{background:'none',border:'none',color:'white',opacity:.7,fontSize:'13px',cursor:'pointer',marginBottom:'8px',display:'block'}}>← Back</button>
        <div style={{fontFamily:'sans-serif',fontSize:'18px',fontWeight:'800'}}>{item.label} Dumpster</div>
        <div style={{fontSize:'13px',opacity:.8,marginTop:'4px'}}>Total: ${total}</div>
      </div>
      <div style={{padding:'20px',maxWidth:'480px',margin:'0 auto'}}>
        <div style={{fontFamily:'sans-serif',fontSize:'18px',fontWeight:'800',marginBottom:'4px'}}>Your information</div>
        <div style={{fontSize:'13px',color:'#6B6B6B',marginBottom:'20px'}}>Required to confirm your delivery.</div>

        <div style={{marginBottom:'12px'}}>
          <label style={labelStyle}>Full name *</label>
          <input style={fieldStyle} placeholder="John Smith" value={name} onChange={function(e){setName(e.target.value);}}/>
        </div>
        <div style={{marginBottom:'12px'}}>
          <label style={labelStyle}>Company name (optional)</label>
          <input style={fieldStyle} placeholder="ABC Construction" value={company} onChange={function(e){setCompany(e.target.value);}}/>
        </div>
        <div style={{marginBottom:'12px'}}>
          <label style={labelStyle}>Email *</label>
          <input style={fieldStyle} placeholder="john@email.com" type="email" value={email} onChange={function(e){setEmail(e.target.value);}}/>
        </div>
        <div style={{marginBottom:'8px'}}>
          <label style={labelStyle}>Phone number *</label>
          <input style={fieldStyle} placeholder="(555) 555-5555" type="tel" value={phone} onChange={function(e){setPhone(e.target.value);}}/>
        </div>
        <div style={{display:'flex',alignItems:'flex-start',gap:'10px',marginBottom:'14px',padding:'10px 12px',background:'#f8f8f6',borderRadius:'8px',border:'1px solid #e0e0da'}}>
          <input
            id="sms-consent"
            type="checkbox"
            checked={smsConsent}
            onChange={function(e){setSmsConsent(e.target.checked);}}
            style={{marginTop:'2px',width:'16px',height:'16px',flexShrink:0,accentColor:'#1A7A4A',cursor:'pointer'}}
          />
          <label htmlFor="sms-consent" style={{fontSize:'12px',color:'#374151',lineHeight:'1.5',cursor:'pointer'}}>
            I agree to receive SMS text messages from Tambo about my order (confirmations, delivery/pickup updates) and account. Message and data rates may apply. Message frequency varies. Reply STOP to opt out, HELP for help. See our{' '}
            <a href="/privacy" style={{color:'#1A7A4A',textDecoration:'underline'}}>Privacy Policy</a>
            {' '}and{' '}
            <a href="/terms" style={{color:'#1A7A4A',textDecoration:'underline'}}>Terms of Service</a>.
          </label>
        </div>
        <div style={{fontSize:'11px',fontWeight:'700',color:'#1A7A4A',textTransform:'uppercase',letterSpacing:'.5px',marginBottom:'8px',marginTop:'4px'}}>
          Delivery / Service Address — where your dumpster will be delivered
        </div>
        <div style={{marginBottom:'12px'}}>
          <label style={labelStyle}>Street address *</label>
          <input style={fieldStyle} placeholder="123 Main St" value={streetAddress} onChange={function(e){setStreetAddress(e.target.value);}}/>
        </div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px',marginBottom:'12px'}}>
          <div>
            <label style={labelStyle}>City *</label>
            <input style={fieldStyle} placeholder="Harlingen" value={city} onChange={function(e){setCity(e.target.value);}}/>
          </div>
          <div>
            <label style={labelStyle}>State *</label>
            <input style={fieldStyle} placeholder="TX" value={stateAbbr} onChange={function(e){setStateAbbr(e.target.value);}} maxLength={2}/>
          </div>
        </div>
        <div style={{marginBottom:'16px'}}>
          <label style={labelStyle}>ZIP code *</label>
          <input style={fieldStyle} placeholder="78550" value={zip} onChange={function(e){setZip(e.target.value);}} maxLength={5}/>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:'10px',marginBottom:'16px',padding:'10px 12px',background:'#f8f8f6',borderRadius:'8px',border:'1px solid #e0e0da'}}>
          <input
            id="billing-same"
            type="checkbox"
            checked={billingSameAsService}
            onChange={function(e){setBillingSameAsService(e.target.checked);}}
            style={{width:'16px',height:'16px',flexShrink:0,accentColor:'#1A7A4A',cursor:'pointer'}}
          />
          <label htmlFor="billing-same" style={{fontSize:'13px',color:'#374151',cursor:'pointer'}}>
            Billing address is the same as service address
          </label>
        </div>
        {!billingSameAsService && (
          <div style={{marginBottom:'16px',padding:'14px',background:'#F9F9F7',borderRadius:'10px',border:'1px solid #E0E0DA'}}>
            <div style={{fontSize:'11px',fontWeight:'700',color:'#6B6B6B',textTransform:'uppercase',letterSpacing:'.5px',marginBottom:'12px'}}>
              Billing Address
            </div>
            <div style={{marginBottom:'12px'}}>
              <label style={labelStyle}>Billing street *</label>
              <input style={fieldStyle} placeholder="456 Billing Ave" value={billingStreet} onChange={function(e){setBillingStreet(e.target.value);}}/>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px',marginBottom:'12px'}}>
              <div>
                <label style={labelStyle}>City *</label>
                <input style={fieldStyle} placeholder="McAllen" value={billingCity} onChange={function(e){setBillingCity(e.target.value);}}/>
              </div>
              <div>
                <label style={labelStyle}>State *</label>
                <input style={fieldStyle} placeholder="TX" value={billingState} onChange={function(e){setBillingState(e.target.value);}} maxLength={2}/>
              </div>
            </div>
            <div>
              <label style={labelStyle}>ZIP code *</label>
              <input style={fieldStyle} placeholder="78501" value={billingZip} onChange={function(e){setBillingZip(e.target.value);}} maxLength={5}/>
            </div>
          </div>
        )}
        {is7yd && (
          <div style={{background:'#FFF7ED',border:'1px solid #FED7AA',borderRadius:'8px',padding:'10px 12px',marginBottom:'12px',fontSize:'13px',color:'#92400E'}}>
            <strong>7-yard — Concrete &amp; heavy debris only.</strong> This size is not available for household, construction junk, or mixed debris.
          </div>
        )}
        <div style={{marginBottom:'12px'}}>
          <label style={labelStyle}>Type of waste *</label>
          <select style={{...fieldStyle,appearance:'none'}} value={wasteType} onChange={function(e){setWasteType(e.target.value);}}>
            <option value="">Select waste type...</option>
            {(is7yd ? CONCRETE_WASTE_TYPES : WASTE_TYPES).map(function(w) { return <option key={w} value={w}>{w}</option>; })}
          </select>
        </div>
        {error && <div style={{background:'#FEE2E2',color:'#DC2626',borderRadius:'8px',padding:'10px',marginBottom:'12px',fontSize:'13px'}}>{error}</div>}
        <div style={{background:'#E8F5EE',borderRadius:'10px',padding:'12px',fontSize:'13px',color:'#1A7A4A',marginBottom:'16px'}}>
          Next: payment. Total due: <strong>${total}</strong>
        </div>
        <button onClick={handleNext} style={{width:'100%',padding:'14px',background:'#0D0D0D',color:'white',border:'none',borderRadius:'10px',fontSize:'15px',fontWeight:'700',cursor:'pointer',marginBottom:'10px'}}>
          Continue to Payment
        </button>
        <button onClick={onCancel} style={{width:'100%',padding:'13px',background:'white',color:'#6B6B6B',border:'1.5px solid #E0E0DA',borderRadius:'10px',fontSize:'14px',cursor:'pointer'}}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function PaymentForm({ amount, subtotal, tax, onSuccess, onPayLaterSuccess, onCancel, orderData, clientProfile, paymentIntentId }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handlePay = async function() {
    if (!stripe || !elements || submitted) return;
    if (!orderData.hauler_id) {
      setError('Your hauler selection was lost. Please go back and reselect a dumpster.');
      return;
    }
    setSubmitted(true);
    setLoading(true);
    setError('');
    try {
      // Step 1: confirm payment on Stripe's side
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: window.location.origin },
        redirect: 'if_required',
      });
      if (result.error) {
        setError(result.error.message);
        setSubmitted(false);
        setLoading(false);
        return;
      }
      // Step 2: payment succeeded — create the order
      const res = await fetch(BACKEND + '/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.assign({}, orderData, { payment_intent_id: paymentIntentId })),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); setSubmitted(false); setLoading(false); return; }
      onSuccess(data.order);
    } catch (err) {
      setError('Payment failed. Please try again.');
      setSubmitted(false);
    }
    setLoading(false);
  };

  const handlePayLater = async function() {
    if (submitted) return;
    if (!orderData.hauler_id) {
      setError('Your hauler selection was lost. Please go back and reselect a dumpster.');
      return;
    }
    setSubmitted(true);
    setLoading(true);
    setError('');
    try {
      const res = await fetch(BACKEND + '/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(Object.assign({}, orderData, { payment_method: 'pay_later' })),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); setLoading(false); setSubmitted(false); return; }
      if (onPayLaterSuccess) onPayLaterSuccess(data.order);
    } catch (err) {
      setError('Could not place order. Please try again.');
      setSubmitted(false);
    }
    setLoading(false);
  };

  const isApprovedPayLater = clientProfile && clientProfile.pay_later_approved === true;
  const hasSavedCard = clientProfile && clientProfile.has_card_on_file;

  return (
    <div style={{padding:'20px',maxWidth:'480px',margin:'0 auto'}}>
      <div style={{fontFamily:'sans-serif',fontSize:'18px',fontWeight:'800',marginBottom:'16px'}}>Complete your payment</div>
      <div style={{background:'#F2F2F0',borderRadius:'10px',padding:'14px',marginBottom:'16px'}}>
        {subtotal != null && tax != null ? (<>
          <div style={{display:'flex',justifyContent:'space-between',fontSize:'13px',marginBottom:'6px'}}>
            <span style={{color:'#6B6B6B'}}>Subtotal</span><span>${subtotal.toFixed(2)}</span>
          </div>
          <div style={{display:'flex',justifyContent:'space-between',fontSize:'13px',marginBottom:'6px'}}>
            <span style={{color:'#6B6B6B'}}>Sales Tax (8.25%)</span><span>${tax.toFixed(2)}</span>
          </div>
          <div style={{display:'flex',justifyContent:'space-between',fontSize:'13px',borderTop:'1px solid #D0D0CA',paddingTop:'6px',marginTop:'2px'}}>
            <span style={{color:'#6B6B6B',fontWeight:'700'}}>Total</span>
            <span style={{fontWeight:'700'}}>${amount.toFixed(2)}</span>
          </div>
        </>) : (
          <div style={{display:'flex',justifyContent:'space-between',fontSize:'13px'}}>
            <span style={{color:'#6B6B6B'}}>Total due</span>
            <span style={{fontWeight:'700'}}>${amount}</span>
          </div>
        )}
      </div>
      {hasSavedCard && (
        <div style={{display:'flex',alignItems:'center',gap:'8px',background:'#F0FDF4',border:'1px solid #BBF7D0',borderRadius:'8px',padding:'10px 12px',marginBottom:'12px',fontSize:'13px',color:'#166534'}}>
          <span>✓</span>
          <span>Card on file — select it below or enter a new one</span>
        </div>
      )}
      <div style={{marginBottom:'16px'}}>
        <PaymentElement options={{layout:'tabs'}} />
      </div>
      {error && <div style={{background:'#FEE2E2',color:'#DC2626',borderRadius:'8px',padding:'10px',marginBottom:'12px',fontSize:'13px'}}>{error}</div>}
      <button onClick={handlePay} disabled={loading || !stripe || submitted}
        style={{width:'100%',padding:'14px',background:'#1A7A4A',color:'white',border:'none',borderRadius:'10px',fontSize:'15px',fontWeight:'700',cursor:'pointer',marginBottom:'10px',opacity:(loading||submitted)?0.7:1}}>
        {loading ? 'Processing...' : 'Pay & Book — $' + (typeof amount === 'number' ? amount.toFixed(2) : amount)}
      </button>
      {isApprovedPayLater ? (
        <button onClick={handlePayLater} disabled={loading || submitted}
          style={{width:'100%',padding:'14px',background:'white',color:'#1A7A4A',border:'2px solid #1A7A4A',borderRadius:'10px',fontSize:'15px',fontWeight:'700',cursor:'pointer',marginBottom:'10px'}}>
          {loading ? 'Processing...' : 'Pay Later (Charge on Delivery)'}
        </button>
      ) : (
        <div style={{marginTop:'6px',padding:'12px',background:'#F9FAFB',borderRadius:'10px',border:'1px solid #E5E7EB',textAlign:'center',fontSize:'13px',color:'#6B7280',lineHeight:'1.5'}}>
          Pay Later available for approved business accounts.<br/>
          Call <a href="tel:+19562263903" style={{color:'#1A7A4A',fontWeight:'700',textDecoration:'none'}}>(956) 226-3903</a> to apply.
        </div>
      )}
      <button onClick={onCancel} disabled={loading}
        style={{width:'100%',padding:'13px',background:'white',color:'#6B6B6B',border:'1.5px solid #E0E0DA',borderRadius:'10px',fontSize:'14px',cursor:'pointer',marginTop:'10px'}}>
        Cancel
      </button>
    </div>
  );
}

function SearchMap({ searchCoords, haulers, activeHauler, onHaulerClick }) {
  const mapElRef = useRef(null);
  const gMapRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const didFitRef = useRef(false);
  const [ready, setReady] = useState(!!(window.google && window.google.maps));
  const onHaulerClickRef = useRef(onHaulerClick);
  useEffect(function() { onHaulerClickRef.current = onHaulerClick; });

  useEffect(function() {
    if (ready) return;
    const timer = setInterval(function() {
      if (window.google && window.google.maps) { setReady(true); clearInterval(timer); }
    }, 200);
    return function() { clearInterval(timer); };
  }, [ready]);

  useEffect(function() { didFitRef.current = false; }, [searchCoords, haulers]);

  useEffect(function() {
    if (!ready || !mapElRef.current || !searchCoords) return;

    if (!gMapRef.current) {
      gMapRef.current = new window.google.maps.Map(mapElRef.current, {
        center: searchCoords, zoom: 11,
        mapTypeControl: false, streetViewControl: false, fullscreenControl: false, clickableIcons: false,
      });
      infoWindowRef.current = new window.google.maps.InfoWindow();
      gMapRef.current.addListener('click', function() { infoWindowRef.current.close(); });
      gMapRef.current.addListener('zoom_changed', function() {
        if (gMapRef.current.getZoom() < 9) infoWindowRef.current.close();
      });
    }

    markersRef.current.forEach(function(m) { m.setMap(null); });
    markersRef.current = [];

    const enc = encodeURIComponent;
    const bluePin = 'data:image/svg+xml;charset=UTF-8,' + enc(
      '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">' +
      '<path d="M14 0C6.3 0 0 6.3 0 14c0 9.8 14 26 14 26S28 23.8 28 14C28 6.3 21.7 0 14 0z" fill="#2563EB" stroke="white" stroke-width="1.5"/>' +
      '<circle cx="14" cy="14" r="6" fill="white"/></svg>'
    );

    const dumpsterIcon = {
      url: 'data:image/svg+xml;charset=UTF-8,' + enc('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 260" width="48" height="30"><rect x="0" y="20" width="500" height="20" rx="3" fill="#145c37"/><rect x="0" y="38" width="500" height="160" rx="4" fill="#1A7A4A"/><rect x="72" y="40" width="8" height="156" rx="2" fill="#145c37"/><rect x="144" y="40" width="8" height="156" rx="2" fill="#145c37"/><rect x="216" y="40" width="8" height="156" rx="2" fill="#145c37"/><rect x="288" y="40" width="8" height="156" rx="2" fill="#145c37"/><rect x="360" y="40" width="8" height="156" rx="2" fill="#145c37"/><rect x="432" y="40" width="8" height="156" rx="2" fill="#145c37"/><rect x="10" y="52" width="28" height="36" rx="3" fill="#145c37"/><rect x="462" y="52" width="28" height="36" rx="3" fill="#145c37"/><rect x="20" y="198" width="120" height="12" rx="4" fill="#145c37"/><rect x="360" y="198" width="120" height="12" rx="4" fill="#145c37"/><rect x="0" y="38" width="500" height="160" rx="4" fill="none" stroke="#0d3d22" stroke-width="4"/></svg>'),
      scaledSize: new window.google.maps.Size(48, 30),
      anchor: new window.google.maps.Point(24, 30),
    };
    const dumpsterIconActive = {
      url: 'data:image/svg+xml;charset=UTF-8,' + enc('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 260" width="58" height="36"><rect x="0" y="20" width="500" height="20" rx="3" fill="#0f5c2a"/><rect x="0" y="38" width="500" height="160" rx="4" fill="#15803d"/><rect x="72" y="40" width="8" height="156" rx="2" fill="#0f5c2a"/><rect x="144" y="40" width="8" height="156" rx="2" fill="#0f5c2a"/><rect x="216" y="40" width="8" height="156" rx="2" fill="#0f5c2a"/><rect x="288" y="40" width="8" height="156" rx="2" fill="#0f5c2a"/><rect x="360" y="40" width="8" height="156" rx="2" fill="#0f5c2a"/><rect x="432" y="40" width="8" height="156" rx="2" fill="#0f5c2a"/><rect x="10" y="52" width="28" height="36" rx="3" fill="#0f5c2a"/><rect x="462" y="52" width="28" height="36" rx="3" fill="#0f5c2a"/><rect x="20" y="198" width="120" height="12" rx="4" fill="#0f5c2a"/><rect x="360" y="198" width="120" height="12" rx="4" fill="#0f5c2a"/><rect x="0" y="38" width="500" height="160" rx="4" fill="none" stroke="#072015" stroke-width="4"/></svg>'),
      scaledSize: new window.google.maps.Size(58, 36),
      anchor: new window.google.maps.Point(29, 36),
    };

    const searchMarker = new window.google.maps.Marker({
      position: searchCoords, map: gMapRef.current, title: 'Your location', zIndex: 20,
      icon: { url: bluePin, scaledSize: new window.google.maps.Size(28, 40), anchor: new window.google.maps.Point(14, 40) },
    });
    markersRef.current.push(searchMarker);

    const bounds = new window.google.maps.LatLngBounds();
    bounds.extend(searchCoords);

    haulers.forEach(function(hauler) {
      if (hauler.base_lat == null || hauler.base_lng == null) return;
      const pos = { lat: hauler.base_lat, lng: hauler.base_lng };
      const isActive = activeHauler === hauler.hauler_id;
      const displayName = hauler.first_name || 'Local Provider';
      const marker = new window.google.maps.Marker({
        position: pos, map: gMapRef.current, title: displayName,
        icon: isActive ? dumpsterIconActive : dumpsterIcon,
        zIndex: isActive ? 15 : 10,
      });
      // Build size+price lines. Step-1 mapHaulers send hauler.sizes=[{size,price_3day,quantity,...}];
      // step-2 haulerResults send a single hauler.size+hauler.price_3day at the top level.
      // All prices are marked-up client-facing rates from the backend — never raw hauler rates.
      var sizePriceLines = '';
      if (hauler.sizes && hauler.sizes.length > 0) {
        sizePriceLines = hauler.sizes.map(function(s) {
          return '<div style="font-size:12px;color:#374151;line-height:1.6">' +
            s.size + ' &mdash; from $' + s.price_3day + '/3d &middot; ' + s.quantity + ' avail</div>';
        }).join('');
      } else if (hauler.size && hauler.price_3day) {
        sizePriceLines = '<div style="font-size:12px;color:#374151;line-height:1.6">' +
          hauler.size + ' &mdash; from $' + hauler.price_3day + '/3d &middot; ' + (hauler.quantity || 0) + ' avail</div>';
      }
      const html =
        '<div style="font-family:sans-serif;padding:10px 10px 8px;min-width:160px;max-width:230px">' +
        '<div style="font-size:13px;font-weight:700;color:#111;margin-bottom:5px">By: ' + displayName + '</div>' +
        sizePriceLines +
        (hauler.distance_miles != null
          ? '<div style="font-size:11px;color:#9ca3af;margin-top:4px">' + hauler.distance_miles + ' mi away</div>'
          : '') +
        '</div>';
      marker.addListener('mouseover', function() {
        if (gMapRef.current.getZoom() < 9) return;
        infoWindowRef.current.setContent(html);
        infoWindowRef.current.open({ anchor: marker, map: gMapRef.current });
      });
      marker.addListener('click', function() {
        if (gMapRef.current.getZoom() < 9) {
          gMapRef.current.panTo(marker.getPosition());
          gMapRef.current.setZoom(10);
          return;
        }
        infoWindowRef.current.setContent(html);
        infoWindowRef.current.open({ anchor: marker, map: gMapRef.current });
        if (onHaulerClickRef.current) onHaulerClickRef.current(hauler);
      });
      bounds.extend(pos);
      markersRef.current.push(marker);
    });

    if (!didFitRef.current && haulers.some(function(h) { return h.base_lat != null; })) {
      gMapRef.current.fitBounds(bounds, 50);
      didFitRef.current = true;
      window.google.maps.event.addListenerOnce(gMapRef.current, 'idle', function() {
        if (gMapRef.current && gMapRef.current.getZoom() > 13) gMapRef.current.setZoom(13);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, searchCoords, haulers, activeHauler]);

  return (
    <div ref={mapElRef} style={{ width: '100%', height: '100%', background: '#e8f0e8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {!ready && <span style={{ color: '#6b7280', fontSize: 13 }}>Loading map...</span>}
    </div>
  );
}

function EstimatorSection({ onBook, zip }) {
  const [mode, setMode] = useState(null); // null | 'scan' | 'roof'

  // Scan state — photos: [{ preview, base64, mediaType }]
  const [photos, setPhotos] = useState([]);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanError, setScanError] = useState('');
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Roof state
  const [roofAddress, setRoofAddress] = useState('');
  const [roofSqft, setRoofSqft] = useState('');
  const [roofStories, setRoofStories] = useState(1);
  const [roofPitch, setRoofPitch] = useState(1.12); // Standard 6/12 default
  const [roofLayers, setRoofLayers] = useState(1);
  const [roofTearOffType, setRoofTearOffType] = useState('shingles'); // 'shingles' | 'full'
  const [roofSatellite, setRoofSatellite] = useState(null); // { roof_sqft, squares } | null
  const [roofSatelliteLoading, setRoofSatelliteLoading] = useState(false);
  const [roofSatelliteNote, setRoofSatelliteNote] = useState(''); // '' | 'unavailable'
  const [roofManualOverride, setRoofManualOverride] = useState(false);

  const PITCH_OPTIONS = [
    { label: 'Flat (2/12)', value: 1.02 },
    { label: 'Walkable (4/12)', value: 1.06 },
    { label: 'Standard (6/12)', value: 1.12 },
    { label: 'Steep (8/12)', value: 1.20 },
    { label: 'Very steep (10/12+)', value: 1.36 },
  ];

  // Normalise any image file (including HEIC from iPhone gallery) to JPEG via canvas.
  // Downscales so the longest side is ≤ 1600 px, quality 0.8.
  function encodeFileAsJpeg(file) {
    return new Promise(function(resolve, reject) {
      var MAX_SIDE = 1600;
      var objectUrl = URL.createObjectURL(file);
      var img = new window.Image();
      img.onload = function() {
        URL.revokeObjectURL(objectUrl);
        var W = img.naturalWidth;
        var H = img.naturalHeight;
        var scale = Math.min(1, MAX_SIDE / Math.max(W || 1, H || 1));
        var w = Math.max(1, Math.round(W * scale));
        var h = Math.max(1, Math.round(H * scale));
        var canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        var dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        resolve({ preview: dataUrl, base64: dataUrl.split(',')[1], mediaType: 'image/jpeg' });
      };
      img.onerror = function() {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('decode_failed'));
      };
      img.src = objectUrl;
    });
  }

  function handleFiles(fileList) {
    if (!fileList || !fileList.length) return;
    var MAX_PHOTOS = 4;
    setScanError('');
    setScanResult(null);

    // Snapshot into a plain array immediately — before the input is cleared by the caller.
    var files = Array.from(fileList);

    Promise.all(files.map(function(file) {
      return encodeFileAsJpeg(file).catch(function() { return null; });
    })).then(function(encoded) {
      var valid = encoded.filter(Boolean);
      var failCount = encoded.length - valid.length;
      if (failCount > 0) {
        setScanError(
          "Couldn't read " + (failCount === 1 ? 'that photo' : failCount + ' photos') +
          ' — try a different one.'
        );
      }
      if (valid.length > 0) {
        // Functional form — never references the `photos` variable in closure.
        setPhotos(function(prev) {
          return [...prev, ...valid].slice(0, MAX_PHOTOS);
        });
      }
    });
  }

  async function analyzeImage() {
    if (!photos.length) return;
    setScanLoading(true); setScanError(''); setScanResult(null);
    try {
      const payload = { images: photos.map(function(p) { return { image: p.base64, mediaType: p.mediaType }; }) };
      if (zip && /^\d{5}$/.test(zip)) payload.zip = zip;
      const res = await fetch(BACKEND + '/api/estimate-load', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.status === 422) {
        setScanError("Couldn't estimate from that photo. Try a clearer shot of the pile.");
        setScanLoading(false); return;
      }
      if (!res.ok || data.error) throw new Error(data.error || 'estimate_failed');
      setScanResult(data);
    } catch (err) {
      setScanError("Something went wrong. Please try again.");
    }
    setScanLoading(false);
  }

  async function measureRoof() {
    if (!roofAddress.trim() || roofSatelliteLoading) return;
    setRoofSatelliteLoading(true);
    setRoofSatellite(null);
    setRoofSatelliteNote('');
    setRoofManualOverride(false);
    try {
      const res = await fetch(BACKEND + '/api/roof-size', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address: roofAddress.trim() }),
      });
      const data = await res.json();
      if (res.ok && data.source === 'satellite') {
        setRoofSatellite(data);
        setRoofSqft(String(data.roof_sqft));
      } else {
        setRoofSatelliteNote('unavailable');
      }
    } catch (err) {
      setRoofSatelliteNote('unavailable');
    }
    setRoofSatelliteLoading(false);
  }

  // Roof math — recomputed each render (pure arithmetic, no side effects)
  let roofResult = null;
  if (mode === 'roof') {
    let squares = null;
    if (roofSatellite && !roofManualOverride) {
      // Satellite path: squares already include pitch + 10% waste from server
      squares = roofSatellite.squares;
    } else {
      const sqft = parseFloat(roofSqft);
      if (sqft > 0) {
        const footprint = sqft / roofStories;
        squares = Math.ceil(footprint * roofPitch * 1.10 / 100);
      }
    }
    if (squares !== null) {
      const effSquares = squares * roofLayers;
      let tons, recs;
      if (roofTearOffType === 'full') {
        // Shingles (250 lb/sq) + decking (150 lb/sq); bulkier → tighter capacity
        tons = Math.round(effSquares * 400 / 2000 * 10) / 10;
        if (effSquares <= 15) recs = [{ size: 10, count: 1 }];
        else if (effSquares <= 23) recs = [{ size: 15, count: 1 }];
        else if (effSquares <= 32) recs = [{ size: 20, count: 1 }];
        else recs = [{ size: 20, count: Math.ceil(effSquares / 32) }];
      } else {
        // Shingles only
        tons = Math.round(effSquares * 250 / 2000 * 10) / 10;
        if (effSquares <= 22) recs = [{ size: 10, count: 1 }];
        else if (effSquares <= 33) recs = [{ size: 15, count: 1 }];
        else if (effSquares <= 45) recs = [{ size: 20, count: 1 }];
        else recs = [{ size: 20, count: Math.ceil(effSquares / 45) }];
      }
      roofResult = { squares, effSquares, tons, recs };
    }
  }

  function modeCardStyle(active) {
    return {
      flex: '1 1 200px', minWidth: '0',
      padding: '20px',
      border: active ? '2px solid #1A7A4A' : '1.5px solid #E0E0DA',
      borderRadius: '12px',
      background: active ? '#F0FBF5' : 'white',
      cursor: 'pointer',
      display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
    };
  }

  function toggleBtnStyle(active) {
    return {
      padding: '7px 16px',
      border: active ? '2px solid #1A7A4A' : '1.5px solid #D0D0CA',
      borderRadius: '8px',
      background: active ? '#1A7A4A' : 'white',
      color: active ? 'white' : '#374151',
      fontSize: '13px', fontWeight: '600', cursor: 'pointer',
    };
  }

  const fLabel = { fontSize: '11px', fontWeight: '700', color: '#6B6B6B', textTransform: 'uppercase', letterSpacing: '.5px', display: 'block', marginBottom: '6px' };
  const fInput = { width: '100%', padding: '10px 12px', border: '1.5px solid #E0E0DA', borderRadius: '8px', fontSize: '14px', outline: 'none', background: 'white', boxSizing: 'border-box' };

  return (
    <section style={{width:'100%',background:'#FAFAF8',fontFamily:'sans-serif',borderBottom:'1px solid #F0F0EC'}}>
      <div style={{maxWidth:'960px',margin:'0 auto',padding:'64px 24px'}}>

        <div style={{fontSize:'11px',fontWeight:'700',color:'#1A7A4A',letterSpacing:'1.5px',textTransform:'uppercase',marginBottom:'10px'}}>
          Free Estimator
        </div>
        <h2 style={{fontSize:'clamp(1.4rem,4vw,1.9rem)',fontWeight:'900',color:'#0D0D0D',margin:'0 0 10px',letterSpacing:'-0.5px'}}>
          Not sure what size?
        </h2>
        <div style={{fontSize:'15px',color:'#666',marginBottom:'36px',maxWidth:'500px',lineHeight:'1.6'}}>
          Scan your load or enter your roof — we&apos;ll tell you exactly what to order.
        </div>

        {/* Mode cards */}
        <div style={{display:'flex',flexWrap:'wrap',gap:'16px',marginBottom: mode ? '28px' : '0'}}>

          <div style={modeCardStyle(mode === 'scan')} onClick={function() { setMode('scan'); setScanResult(null); setScanError(''); setPhotos([]); }}>
            <div style={{width:'48px',height:'48px',background:'#111',borderRadius:'12px',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:'16px',flexShrink:0}}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1D9E75" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="12" cy="12" r="4"/><line x1="3" y1="9" x2="3" y2="9"/><line x1="21" y1="9" x2="21" y2="9"/>
              </svg>
            </div>
            <div style={{fontSize:'11px',fontWeight:'700',color:'#1D9E75',letterSpacing:'1px',textTransform:'uppercase',marginBottom:'6px'}}>01</div>
            <div style={{fontSize:'16px',fontWeight:'900',color:'#0D0D0D',marginBottom:'5px',letterSpacing:'-0.2px'}}>Scan a load</div>
            <div style={{fontSize:'13px',color:'#666',lineHeight:'1.5'}}>Photo of your debris pile</div>
          </div>

          <div style={modeCardStyle(mode === 'roof')} onClick={function() { setMode('roof'); }}>
            <div style={{width:'48px',height:'48px',background:'#111',borderRadius:'12px',display:'flex',alignItems:'center',justifyContent:'center',marginBottom:'16px',flexShrink:0}}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1D9E75" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="3 9 12 2 21 9 21 20 3 20 3 9"/>
              </svg>
            </div>
            <div style={{fontSize:'11px',fontWeight:'700',color:'#1D9E75',letterSpacing:'1px',textTransform:'uppercase',marginBottom:'6px'}}>02</div>
            <div style={{fontSize:'16px',fontWeight:'900',color:'#0D0D0D',marginBottom:'5px',letterSpacing:'-0.2px'}}>Roof tear-off</div>
            <div style={{fontSize:'13px',color:'#666',lineHeight:'1.5'}}>Squares &amp; dumpsters by house size</div>
          </div>

        </div>

        {/* ── Scan mode ── */}
        {mode === 'scan' && (
          <div>
            {/* Hidden inputs: camera (capture) and gallery (multiple, no capture) */}
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{display:'none'}} onChange={function(e) { handleFiles(e.target.files); e.target.value = ''; }} />
            <input ref={galleryInputRef} type="file" accept="image/*" multiple style={{display:'none'}} onChange={function(e) { handleFiles(e.target.files); e.target.value = ''; }} />

            {/* Empty state — show primary + secondary buttons */}
            {photos.length === 0 && !scanLoading && (
              <div style={{display:'flex',flexDirection:'column',gap:'10px',marginBottom:'14px'}}>
                <button
                  onClick={function() { cameraInputRef.current && cameraInputRef.current.click(); }}
                  style={{width:'100%',padding:'14px',background:'#1A7A4A',color:'white',border:'none',borderRadius:'10px',fontSize:'15px',fontWeight:'700',cursor:'pointer'}}
                >
                  📷 Take a photo
                </button>
                <button
                  onClick={function() { galleryInputRef.current && galleryInputRef.current.click(); }}
                  style={{width:'100%',padding:'13px',background:'white',color:'#1A7A4A',border:'2px solid #1A7A4A',borderRadius:'10px',fontSize:'15px',fontWeight:'700',cursor:'pointer'}}
                >
                  Upload photos
                </button>
                <div style={{fontSize:'12px',color:'#999',textAlign:'center'}}>JPEG, PNG, or WebP · max 8 MB each</div>
              </div>
            )}

            {/* Photos added — thumbnails + add-more + estimate button */}
            {photos.length > 0 && !scanResult && !scanLoading && (
              <div>
                {/* Thumbnail row */}
                <div style={{display:'flex',gap:'10px',flexWrap:'wrap',marginBottom:'12px'}}>
                  {photos.map(function(p, i) {
                    return (
                      <div key={i} style={{position:'relative',display:'inline-block',flexShrink:0}}>
                        <img src={p.preview} alt={'Photo ' + (i + 1)} style={{width:'80px',height:'80px',objectFit:'cover',borderRadius:'8px',display:'block'}}/>
                        <button
                          onClick={function() { setPhotos(function(prev) { return prev.filter(function(_, j) { return j !== i; }); }); setScanResult(null); setScanError(''); }}
                          style={{position:'absolute',top:'-7px',right:'-7px',width:'20px',height:'20px',borderRadius:'50%',background:'#111',color:'white',border:'none',cursor:'pointer',fontSize:'11px',display:'flex',alignItems:'center',justifyContent:'center',padding:0,lineHeight:1}}
                        >
                          ✕
                        </button>
                      </div>
                    );
                  })}
                </div>

                {/* Add-more controls (when under limit) */}
                {photos.length < 4 && (
                  <div style={{marginBottom:'14px'}}>
                    <div style={{fontSize:'12px',color:'#666',marginBottom:'8px'}}>
                      Add angles of the same pile, or photos of multiple piles (up to 4)
                    </div>
                    <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                      <button
                        onClick={function() { cameraInputRef.current && cameraInputRef.current.click(); }}
                        style={{padding:'7px 14px',background:'#F0FBF5',color:'#1A7A4A',border:'1.5px solid #BBE7CC',borderRadius:'8px',fontSize:'13px',fontWeight:'600',cursor:'pointer'}}
                      >
                        📷 Take another
                      </button>
                      <button
                        onClick={function() { galleryInputRef.current && galleryInputRef.current.click(); }}
                        style={{padding:'7px 14px',background:'white',color:'#374151',border:'1.5px solid #D0D0CA',borderRadius:'8px',fontSize:'13px',fontWeight:'600',cursor:'pointer'}}
                      >
                        + Add from gallery
                      </button>
                    </div>
                  </div>
                )}

                <button
                  onClick={analyzeImage}
                  style={{width:'100%',padding:'13px',background:'#0D0D0D',color:'white',border:'none',borderRadius:'10px',fontSize:'15px',fontWeight:'700',cursor:'pointer'}}
                >
                  Estimate my dumpster
                </button>
              </div>
            )}

            {scanLoading && (
              <div style={{textAlign:'center',padding:'24px',fontSize:'14px',color:'#6B6B6B'}}>
                🔍 Analyzing {photos.length > 1 ? photos.length + ' photos' : 'your photo'}…
              </div>
            )}

            {scanError && (
              <div style={{background:'#FEF2F2',color:'#DC2626',borderRadius:'8px',padding:'10px 12px',fontSize:'13px',margin:'12px 0'}}>
                {scanError}
              </div>
            )}

            {scanResult && !scanLoading && (function() {
              var es = (navigator.language || '').toLowerCase().startsWith('es');
              var noResult = scanResult.noResult === true || !scanResult.recommendedSize;
              var startOver = (
                <button
                  onClick={function() { setPhotos([]); setScanResult(null); setScanError(''); }}
                  style={{width:'100%',padding:'10px',background:'none',color:'#888',border:'1.5px solid #E0E0DA',borderRadius:'10px',fontSize:'13px',fontWeight:'600',cursor:'pointer',marginTop:'8px'}}
                >
                  {es ? 'Empezar de nuevo' : 'Start over'}
                </button>
              );
              if (noResult) {
                return (
                  <div style={{background:'#F9F9F7',border:'1.5px solid #E0E0DA',borderRadius:'12px',padding:'20px',marginTop:'4px',textAlign:'center'}}>
                    <div style={{fontSize:'28px',marginBottom:'10px'}}>🤔</div>
                    <div style={{fontSize:'15px',fontWeight:'700',color:'#374151',marginBottom:'8px'}}>
                      {es ? 'No pudimos ver un montón de escombros en estas fotos.' : "We couldn't spot a debris pile in these photos."}
                    </div>
                    <div style={{fontSize:'13px',color:'#666',lineHeight:'1.6',marginBottom:'4px'}}>
                      {scanResult.notes || (es ? 'Intente una foto más clara del material — de frente, buena iluminación, con el montón llenando la mayor parte del encuadre.' : 'Try a clearer shot of the load — straight-on, good lighting, with the pile filling most of the frame.')}
                    </div>
                    {startOver}
                  </div>
                );
              }
              var low = scanResult.estimatedYards ? scanResult.estimatedYards.low : 0;
              var high = scanResult.estimatedYards ? scanResult.estimatedYards.high : 0;
              var n = scanResult.multipleHauls || 1;
              var confirmed = scanResult.availabilityConfirmed !== false;
              return (
                <div style={{background:'#F0FBF5',border:'1.5px solid #BBE7CC',borderRadius:'12px',padding:'20px',marginTop:'4px'}}>
                  <div style={{fontSize:'11px',fontWeight:'700',color:'#1A7A4A',letterSpacing:'1px',textTransform:'uppercase',marginBottom:'8px'}}>
                    {es ? 'Estimado' : 'Estimate'}
                  </div>
                  <div style={{fontSize:'clamp(1.4rem,4vw,1.8rem)',fontWeight:'900',color:'#0D0D0D',marginBottom:'6px',letterSpacing:'-0.5px'}}>
                    {scanResult.recommendedSize} yard
                  </div>
                  <div style={{fontSize:'13px',color:'#374151',marginBottom:'10px'}}>
                    {confirmed
                      ? (es
                          ? 'Sugerido para aproximadamente ' + low + '–' + high + ' yardas cúbicas de ' + scanResult.material + '.'
                          : 'Suggested for about ' + low + '–' + high + ' cubic yards of ' + scanResult.material + '.')
                      : (es
                          ? 'Según tus fotos, un contenedor de ' + scanResult.recommendedSize + ' yardas es lo indicado. Confirmaremos disponibilidad en tu zona al reservar.'
                          : 'Based on your photos, a ' + scanResult.recommendedSize + ' yard looks right. We\'ll confirm availability in your area when you book.')}
                  </div>

                  {scanResult.sizedUp && (
                    <div style={{fontSize:'12px',color:'#374151',background:'#F3F4F6',borderRadius:'6px',padding:'6px 10px',marginBottom:'8px',lineHeight:'1.5'}}>
                      {es
                        ? 'Sugerimos un tamaño más grande. Un poco de espacio extra cuesta menos que un segundo viaje.'
                        : 'We suggest sizing up. A little extra room costs less than a second haul.'}
                    </div>
                  )}

                  {scanResult.fillLimit && (
                    <div style={{fontSize:'12px',fontWeight:'700',color:'#B45309',background:'#FEF3C7',borderRadius:'6px',padding:'6px 10px',marginBottom:'8px',lineHeight:'1.5'}}>
                      {es
                        ? 'Material pesado — no lo llene más de la mitad. El límite de peso se alcanza antes de llenar la caja.'
                        : 'Heavy material — do not fill past the halfway mark. Weight limits are reached before the box is full.'}
                    </div>
                  )}

                  {n > 1 && (
                    <div style={{fontSize:'12px',color:'#374151',background:'#F3F4F6',borderRadius:'6px',padding:'6px 10px',marginBottom:'8px',lineHeight:'1.5'}}>
                      {es
                        ? 'Esto parece unas ' + n + ' cargas. Puede reservar una y enviar SWAP por mensaje cuando esté llena.'
                        : 'This looks like about ' + n + ' loads. You can book one and text SWAP when it\'s full.'}
                    </div>
                  )}

                  {scanResult.confidence === 'low' && (
                    <div style={{fontSize:'12px',color:'#374151',background:'#F3F4F6',borderRadius:'6px',padding:'6px 10px',marginBottom:'8px',lineHeight:'1.5'}}>
                      {es
                        ? 'Difícil de estimar con esta foto — sugerimos un tamaño mayor por seguridad. Llame al (956) 226-3903 si desea una segunda opinión.'
                        : 'Hard to tell from this photo — we sized up to be safe. Call (956) 226-3903 if you want a second opinion.'}
                    </div>
                  )}

                  <button
                    onClick={function() { onBook(scanResult.recommendedSize, null); }}
                    style={{width:'100%',padding:'12px',background:'#1A7A4A',color:'white',border:'none',borderRadius:'10px',fontSize:'14px',fontWeight:'700',cursor:'pointer',marginTop:'6px',display:'block'}}
                  >
                    {es ? 'Reservar ' + scanResult.recommendedSize + ' yardas →' : 'Book ' + scanResult.recommendedSize + ' yard →'}
                  </button>
                  {startOver}
                </div>
              );
            })()}
          </div>
        )}

        {/* ── Roof mode ── */}
        {mode === 'roof' && (
          <div>

            {/* ── Address + satellite measurement ── */}
            <div style={{marginBottom:'16px'}}>
              <label style={fLabel}>Job address</label>
              <div style={{display:'flex',gap:'8px'}}>
                <input
                  style={{...fInput, flex:'1'}}
                  type="text"
                  placeholder="123 Main St, City, TX"
                  value={roofAddress}
                  onChange={function(e) {
                    setRoofAddress(e.target.value);
                    if (roofSatellite || roofSatelliteNote) {
                      setRoofSatellite(null); setRoofSatelliteNote(''); setRoofManualOverride(false);
                    }
                  }}
                  onKeyDown={function(e) { if (e.key === 'Enter') measureRoof(); }}
                />
                <button
                  onClick={measureRoof}
                  disabled={!roofAddress.trim() || roofSatelliteLoading}
                  style={{padding:'10px 14px',background:'#0D0D0D',color:'white',border:'none',borderRadius:'8px',fontSize:'13px',fontWeight:'700',cursor: roofAddress.trim() && !roofSatelliteLoading ? 'pointer' : 'not-allowed',opacity: roofAddress.trim() && !roofSatelliteLoading ? 1 : 0.45,whiteSpace:'nowrap',flexShrink:0}}
                >
                  {roofSatelliteLoading ? 'Measuring…' : '📡 Measure'}
                </button>
              </div>

              {/* Satellite success banner */}
              {roofSatellite && !roofManualOverride && (
                <div style={{marginTop:'8px',padding:'8px 12px',background:'#F0FBF5',border:'1px solid #BBE7CC',borderRadius:'8px',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'8px',flexWrap:'wrap'}}>
                  <span style={{fontSize:'13px',color:'#1A7A4A',fontWeight:'600'}}>
                    📡 Roof measured from satellite: ~{roofSatellite.squares} squares ({roofSatellite.roof_sqft.toLocaleString()} sqft)
                  </span>
                  <button
                    onClick={function() { setRoofManualOverride(true); }}
                    style={{fontSize:'12px',color:'#666',background:'none',border:'none',cursor:'pointer',padding:'0',textDecoration:'underline',flexShrink:0}}
                  >
                    Edit manually
                  </button>
                </div>
              )}

              {/* Unavailable note */}
              {roofSatelliteNote === 'unavailable' && (
                <div style={{marginTop:'6px',fontSize:'12px',color:'#888'}}>
                  Couldn&apos;t measure this roof from satellite — enter details below.
                </div>
              )}
            </div>

            {/* ── Tear-off type (always shown) ── */}
            <div style={{marginBottom:'16px'}}>
              <label style={fLabel}>What&apos;s coming off?</label>
              <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                <button style={toggleBtnStyle(roofTearOffType === 'shingles')} onClick={function() { setRoofTearOffType('shingles'); }}>
                  Shingles only
                </button>
                <button style={toggleBtnStyle(roofTearOffType === 'full')} onClick={function() { setRoofTearOffType('full'); }}>
                  Full tear-off (shingles + decking)
                </button>
              </div>
            </div>

            {/* ── Manual inputs — hidden when satellite data is live ── */}
            {(!roofSatellite || roofManualOverride) && (
              <div>
                <div style={{marginBottom:'16px'}}>
                  <label style={fLabel}>House footprint (sqft) *</label>
                  <input style={fInput} type="number" placeholder="2000" min="100" value={roofSqft} onChange={function(e) { setRoofSqft(e.target.value); }} />
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:'16px',marginBottom:'16px'}}>
                  <div>
                    <label style={fLabel}>Stories</label>
                    <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                      {[1, 2].map(function(n) {
                        return (
                          <button key={n} style={toggleBtnStyle(roofStories === n)} onClick={function() { setRoofStories(n); }}>
                            {n === 1 ? '1 story' : '2 stories'}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <label style={fLabel}>Roof pitch</label>
                    <select style={{...fInput, cursor:'pointer'}} value={roofPitch} onChange={function(e) { setRoofPitch(parseFloat(e.target.value)); }}>
                      {PITCH_OPTIONS.map(function(p) {
                        return <option key={p.value} value={p.value}>{p.label}</option>;
                      })}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* ── Shingle layers (always shown once we have a measurement path) ── */}
            {(roofSatellite && !roofManualOverride ? true : parseFloat(roofSqft) > 0) && (
              <div style={{marginBottom:'20px'}}>
                <label style={fLabel}>Shingle layers</label>
                <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                  {[1, 2, 3].map(function(n) {
                    return (
                      <button key={n} style={toggleBtnStyle(roofLayers === n)} onClick={function() { setRoofLayers(n); }}>
                        {n}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Result card ── */}
            {roofResult && (
              <div style={{background:'#F0FBF5',border:'1.5px solid #BBE7CC',borderRadius:'12px',padding:'20px'}}>
                <div style={{fontSize:'11px',fontWeight:'700',color:'#1A7A4A',letterSpacing:'1px',textTransform:'uppercase',marginBottom:'8px'}}>Estimate</div>
                <div style={{fontSize:'clamp(1.4rem,4vw,1.8rem)',fontWeight:'900',color:'#1A7A4A',marginBottom:'8px',letterSpacing:'-0.5px'}}>
                  {roofResult.recs[0].count} × {roofResult.recs[0].size} yd
                </div>
                <div style={{fontSize:'13px',color:'#374151',marginBottom:'4px'}}>
                  <strong>Type:</strong> {roofTearOffType === 'full' ? 'Full tear-off (shingles + decking)' : 'Shingles only'}
                </div>
                <div style={{fontSize:'13px',color:'#374151',marginBottom:'4px'}}>
                  <strong>Squares:</strong> {roofResult.squares}{roofSatellite && !roofManualOverride ? ' (satellite)' : ''} ×{roofLayers} layer{roofLayers > 1 ? 's' : ''} = {roofResult.effSquares} effective
                </div>
                <div style={{fontSize:'13px',color:'#374151',marginBottom:'12px'}}>
                  <strong>Estimated weight:</strong> ~{roofResult.tons} tons
                </div>
                {roofTearOffType === 'full' ? (
                  <div style={{fontSize:'12px',fontWeight:'700',color:'#B45309',background:'#FEF3C7',borderRadius:'6px',padding:'4px 10px',display:'inline-block',marginBottom:'14px'}}>
                    ⚠ Plywood is bulky — load flat sheets first, shingles on top.
                  </div>
                ) : (
                  <div style={{fontSize:'12px',fontWeight:'700',color:'#B45309',background:'#FEF3C7',borderRadius:'6px',padding:'4px 10px',display:'inline-block',marginBottom:'14px'}}>
                    ⚠ Shingles are heavy — don&apos;t fill past the top rail.
                  </div>
                )}
                <button
                  onClick={function() { onBook(roofResult.recs[0].size, roofAddress || null); }}
                  style={{width:'100%',padding:'12px',background:'#1A7A4A',color:'white',border:'none',borderRadius:'10px',fontSize:'14px',fontWeight:'700',cursor:'pointer',display:'block'}}
                >
                  Book this dumpster →
                </button>
              </div>
            )}
          </div>
        )}

      </div>
    </section>
  );
}

function SearchPage({ onSelect, duration, setDuration, onSearch, onSaveSearch, savedSearch, user, onEnvFeeChange }) {
  const [loading, setLoading] = useState(false);
  const [displayLocation, setDisplayLocation] = useState(savedSearch ? savedSearch.displayLocation || '' : '');
  const [searchStep, setSearchStep] = useState(savedSearch ? savedSearch.searchStep || null : null);
  const [resolvedMarket, setResolvedMarket] = useState(savedSearch ? savedSearch.resolvedMarket || null : null);
  const [sizeSummaries, setSizeSummaries] = useState(savedSearch ? savedSearch.sizeSummaries || [] : []);
  const [selectedSize, setSelectedSize] = useState(savedSearch ? savedSearch.selectedSize || null : null);
  const [haulerResults, setHaulerResults] = useState(savedSearch ? savedSearch.haulerResults || [] : []);
  const [mapHaulers, setMapHaulers] = useState(savedSearch ? savedSearch.mapHaulers || [] : []);
  const [haulerLoading, setHaulerLoading] = useState(false);
  const [searchZip, setSearchZip] = useState(savedSearch ? savedSearch.zip || '' : '');
  const [searchLat, setSearchLat] = useState(savedSearch ? savedSearch.lat || null : null);
  const [searchLng, setSearchLng] = useState(savedSearch ? savedSearch.lng || null : null);
  const [activeHauler, setActiveHauler] = useState(null);
  const [sizeSort, setSizeSort] = useState('size-asc');
  const [preferredSize, setPreferredSize] = useState(null);
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);

  useEffect(function() {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_KEY;
    if (!apiKey) return;

    // Inject pac-container fix so dropdown appears above all overlays and accepts clicks
    if (!document.getElementById('pac-style')) {
      const style = document.createElement('style');
      style.id = 'pac-style';
      style.textContent = '.pac-container { z-index: 9999 !important; pointer-events: all !important; }';
      document.head.appendChild(style);
    }

    function initAutocomplete() {
      if (!window.google?.maps?.places) return;
      // Defer one tick to ensure inputRef.current is mounted
      setTimeout(function() {
        if (!inputRef.current) return;
        if (autocompleteRef.current) return; // already initialised
        autocompleteRef.current = new window.google.maps.places.Autocomplete(
          inputRef.current,
          { types: ['geocode'], componentRestrictions: { country: 'us' }, fields: ['address_components', 'formatted_address', 'geometry'] }
        );
        autocompleteRef.current.addListener('place_changed', async function() {
          const place = autocompleteRef.current.getPlace();
          if (!place.address_components) return;
          const addr = extractComponents(place.address_components);
          addr.fullAddress = place.formatted_address || inputRef.current.value;
          if (place.geometry?.location) {
            addr.lat = place.geometry.location.lat();
            addr.lng = place.geometry.location.lng();
          }
          if (addr.zip) {
            runMarketSearch(addr.zip, addr);
          } else if (place.geometry?.viewport) {
            setLoading(true);
            const zips = await getZipsInBounds(place.geometry.viewport);
            if (zips.length) { runMarketSearch(zips[0], addr); }
            else { setSizeSummaries([]); setSearchStep('sizes'); setResolvedMarket(null); setLoading(false); }
          }
        });
      }, 0);
    }

    if (window.google?.maps) {
      initAutocomplete();
    } else if (!document.getElementById('gmaps-script')) {
      window.__gmapsReady = initAutocomplete;
      const s = document.createElement('script');
      s.id = 'gmaps-script';
      s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async&solution_channel=GMP_QB_addressselection_v1&callback=__gmapsReady`;
      document.head.appendChild(s);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore the address input when returning from the detail page
  useEffect(function() {
    if (savedSearch && savedSearch.inputValue && inputRef.current) {
      inputRef.current.value = savedSearch.inputValue;
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function extractComponents(components) {
    const get = function(type) {
      const c = components.find(function(c) { return c.types.includes(type); });
      return c ? c.short_name : '';
    };
    return {
      zip:           get('postal_code'),
      streetAddress: [get('street_number'), get('route')].filter(Boolean).join(' '),
      city:          get('locality') || get('sublocality_level_1') || get('administrative_area_level_2'),
      stateAbbr:     get('administrative_area_level_1'),
    };
  }

  // Reverse-geocode a grid of points within a viewport to discover all ZIP codes in the area
  async function getZipsInBounds(viewport) {
    const ne = viewport.getNorthEast();
    const sw = viewport.getSouthWest();
    const latSpan = ne.lat() - sw.lat();
    const lngSpan = ne.lng() - sw.lng();
    const points = [
      { lat: sw.lat() + latSpan * 0.5,  lng: sw.lng() + lngSpan * 0.5  }, // center
      { lat: sw.lat() + latSpan * 0.25, lng: sw.lng() + lngSpan * 0.25 },
      { lat: sw.lat() + latSpan * 0.75, lng: sw.lng() + lngSpan * 0.25 },
      { lat: sw.lat() + latSpan * 0.25, lng: sw.lng() + lngSpan * 0.75 },
      { lat: sw.lat() + latSpan * 0.75, lng: sw.lng() + lngSpan * 0.75 },
      { lat: sw.lat() + latSpan * 0.5,  lng: sw.lng() + lngSpan * 0.25 },
      { lat: sw.lat() + latSpan * 0.5,  lng: sw.lng() + lngSpan * 0.75 },
    ];
    const geocoder = new window.google.maps.Geocoder();
    const zips = new Set();
    await Promise.all(points.map(function(pt) {
      return new Promise(function(resolve) {
        geocoder.geocode({ location: pt }, function(results, status) {
          if (status === 'OK') {
            results.forEach(function(r) {
              const c = (r.address_components || []).find(function(c) { return c.types.includes('postal_code'); });
              if (c) zips.add(c.short_name);
            });
          }
          resolve();
        });
      });
    }));
    return [...zips];
  }

  async function runMarketSearch(zip, addrData) {
    setLoading(true);
    setSearchStep(null);
    setActiveHauler(null);
    try {
      const params = new URLSearchParams({ zip });
      if (addrData?.lat) params.set('lat', addrData.lat);
      if (addrData?.lng) params.set('lng', addrData.lng);
      const res = await fetch(BACKEND + '/api/orders/market-sizes?' + params.toString());
      const data = await res.json();
      const market = data.market_name || null;
      const sizes = data.sizes || [];
      const pins = data.mapHaulers || [];
      const fee = data.environmental_fee || 0;
      const displayLoc = addrData?.city
        ? addrData.city + (addrData.stateAbbr ? ', ' + addrData.stateAbbr : '')
        : zip;
      setResolvedMarket(market);
      setSizeSummaries(sizes);
      setMapHaulers(pins);
      if (onEnvFeeChange) onEnvFeeChange(fee);
      setSearchZip(zip);
      setSearchLat(addrData?.lat || null);
      setSearchLng(addrData?.lng || null);
      setDisplayLocation(displayLoc);
      setSearchStep('sizes');
      setSelectedSize(null);
      setHaulerResults([]);
      if (onSearch) onSearch({ zip, fullAddress: addrData?.fullAddress, streetAddress: addrData?.streetAddress || '', city: addrData?.city || '', stateAbbr: addrData?.stateAbbr || '', lat: addrData?.lat || null, lng: addrData?.lng || null });
      if (onSaveSearch) onSaveSearch({ zip, lat: addrData?.lat || null, lng: addrData?.lng || null, displayLocation: displayLoc, inputValue: inputRef.current?.value || '', resolvedMarket: market, sizeSummaries: sizes, mapHaulers: pins, searchStep: 'sizes', selectedSize: null, haulerResults: [] });
    } catch (err) {
      setSizeSummaries([]);
      setResolvedMarket(null);
      setSearchStep('sizes');
    }
    setLoading(false);
  }

  async function loadHaulersForSize(size) {
    if (!resolvedMarket) return;
    setSelectedSize(size);
    setSearchStep('haulers');
    setHaulerLoading(true);
    setHaulerResults([]);
    try {
      const params = new URLSearchParams({ market: resolvedMarket, size });
      if (searchZip) params.set('zip', searchZip);
      if (searchLat) params.set('lat', searchLat);
      if (searchLng) params.set('lng', searchLng);
      const res = await fetch(BACKEND + '/api/orders/market-haulers?' + params.toString());
      const data = await res.json();
      const results = data.haulers || [];
      setHaulerResults(results);
      if (onSaveSearch) onSaveSearch({ zip: searchZip, lat: searchLat, lng: searchLng, displayLocation, inputValue: inputRef.current?.value || '', resolvedMarket, sizeSummaries, mapHaulers, searchStep: 'haulers', selectedSize: size, haulerResults: results });
    } catch (err) {
      setHaulerResults([]);
    }
    setHaulerLoading(false);
  }

  async function handleSearch() {
    const input = (inputRef.current?.value || '').trim();
    if (!input) return;

    if (/^\d{5}$/.test(input)) {
      runMarketSearch(input, { zip: input, fullAddress: input, streetAddress: '', city: '', stateAbbr: '' });
      return;
    }

    if (!window.google?.maps) {
      runMarketSearch(input, { zip: input, fullAddress: input, streetAddress: '', city: '', stateAbbr: '' });
      return;
    }

    setLoading(true);
    new window.google.maps.Geocoder().geocode(
      { address: input, componentRestrictions: { country: 'US' } },
      async function(geocodeResults, status) {
        if (status === 'OK' && geocodeResults[0]) {
          const addr = extractComponents(geocodeResults[0].address_components);
          addr.fullAddress = geocodeResults[0].formatted_address;
          if (geocodeResults[0].geometry?.location) {
            addr.lat = geocodeResults[0].geometry.location.lat();
            addr.lng = geocodeResults[0].geometry.location.lng();
          }
          if (addr.zip) {
            runMarketSearch(addr.zip, addr);
          } else if (geocodeResults[0].geometry?.viewport) {
            const zips = await getZipsInBounds(geocodeResults[0].geometry.viewport);
            if (zips.length) { runMarketSearch(zips[0], addr); }
            else { setSizeSummaries([]); setSearchStep('sizes'); setResolvedMarket(null); setLoading(false); }
          } else {
            setSizeSummaries([]); setSearchStep('sizes'); setResolvedMarket(null); setLoading(false);
          }
        } else {
          setSizeSummaries([]); setSearchStep('sizes'); setResolvedMarket(null); setLoading(false);
        }
      }
    );
  }

  const selectedSizeSummary = sizeSummaries.find(function(s) { return s.size === selectedSize; });
  const searchCoords = (searchLat && searchLng) ? { lat: searchLat, lng: searchLng } : null;

  const durTabs = (
    <div className="dur-tabs">
      <button className={duration === '3day' ? 'active' : ''} onClick={function() { setDuration('3day'); }}>3 day</button>
      <button className={duration === '5day' ? 'active' : ''} onClick={function() { setDuration('5day'); }}>5 day</button>
      <button className={duration === '7day' ? 'active' : ''} onClick={function() { setDuration('7day'); }}>7 day</button>
    </div>
  );

  return (
    <div style={{display:'flex',flexDirection:'column',minHeight:'100vh'}}>
      <div className="hero" style={{backgroundImage:`url(${process.env.PUBLIC_URL}/tambo-hero.png)`}}>
        <div className="hero-header" style={{width:'100%',display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'12px'}}>
          <div className="logo" onClick={function(){window.location.href='/';}} style={{cursor:'pointer'}}>Tam<span>bo</span></div>
          {user
            ? <a href="/account" style={{color:'white',fontSize:'13px',fontWeight:'500',textDecoration:'none'}}>My Account</a>
            : <a href="/login" style={{color:'white',fontSize:'13px',fontWeight:'500',textDecoration:'none'}}>Sign in</a>
          }
        </div>
        <p className="hero-tagline" style={{color:'#6EE7B7',fontWeight:'700',fontSize:'18px',textAlign:'center',margin:'0 0 10px'}}>#1 Dumpster Rental Provider</p>
        <h1 style={{margin:0}}>Order Fast. Order Local.</h1>
        <h1 style={{margin:'4px 0 12px',fontSize:'clamp(1.8rem, 7.5vw, 3.5rem)',fontWeight:'900',whiteSpace:'nowrap'}}>Order Tambo Dumpsters.</h1>
        <div style={{fontSize:'13px',color:'rgba(255,255,255,0.72)',marginBottom:'16px',textAlign:'center'}}>
          No account needed — book as guest or <a href="/login" style={{color:'#6EE7B7'}}>sign in</a> for repeat client benefits
        </div>
        <div className="search-box">
          <input
            ref={inputRef}
            type="text"
            placeholder="Enter address, ZIP code, or city &amp; state"
            defaultValue=""
            onChange={function() { setSearchStep(null); }}
            onKeyDown={function(e) {
              if (e.key === 'Enter' && !document.querySelector('.pac-item-selected')) handleSearch();
            }}
          />
          <button onClick={handleSearch}>{loading ? 'Searching...' : 'Search'}</button>
        </div>
      </div>
      {loading && (
        <div style={{textAlign:'center',padding:'60px 20px',color:'#6b7280',fontSize:'15px'}}>
          Searching your area…
        </div>
      )}

      {/* Out of area */}
      {!loading && searchStep === 'sizes' && resolvedMarket === null && (
        <div style={{textAlign:'center',padding:'40px 20px'}}>
          <div style={{marginBottom:'12px'}}><RollOffDumpsterSVG size={72} /></div>
          <div style={{fontSize:'18px',fontWeight:'700',marginBottom:'8px'}}>
            We&apos;re not in {displayLocation || 'your area'} yet
          </div>
          <div style={{fontSize:'13px',color:'#6B6B6B',marginBottom:'12px'}}>
            We&apos;re expanding! Check back soon or give us a call.
          </div>
          <div style={{fontSize:'13px'}}>
            <a href="tel:9562263903" style={{color:'#1A7A4A',fontWeight:'600',textDecoration:'none'}}>(956) 226-3903</a>
          </div>
        </div>
      )}

      {/* In market, no availability */}
      {!loading && searchStep === 'sizes' && resolvedMarket !== null && sizeSummaries.length === 0 && (
        <div style={{textAlign:'center',padding:'40px 20px'}}>
          <div style={{marginBottom:'12px'}}><RollOffDumpsterSVG size={72} /></div>
          <div style={{fontSize:'18px',fontWeight:'700',marginBottom:'8px'}}>No dumpsters available in this area yet</div>
          <div style={{fontSize:'13px',color:'#6B6B6B'}}>We are expanding! Check back soon or try a nearby address or ZIP code.</div>
        </div>
      )}

      {/* Single map + step-conditional content — one SearchMap instance so pins update on step change
          rather than unmounting/remounting when the step switches. */}
      {!loading && ((searchStep === 'sizes' && resolvedMarket !== null && sizeSummaries.length > 0) || searchStep === 'haulers') && (
        <div className={searchCoords ? 'map-stacked-layout' : 'results-wrap'}>
          {searchCoords && (
            <div className="map-panel">
              <SearchMap
                searchCoords={searchCoords}
                haulers={searchStep === 'haulers' ? haulerResults : mapHaulers}
                activeHauler={activeHauler}
                onHaulerClick={function(h) { setActiveHauler(function(prev) { return prev === h.hauler_id ? null : h.hauler_id; }); }}
              />
            </div>
          )}
          <div className={searchCoords ? 'cards-section' : ''}>
            {searchStep === 'sizes' && (
              <>
                <div className="results-header">
                  <div>
                    <h2>Available near {displayLocation}</h2>
                    <p>{sizeSummaries.length} size{sizeSummaries.length !== 1 ? 's' : ''} available — tap a size to see dumpsters</p>
                  </div>
                  {durTabs}
                </div>
                <div style={{display:'flex',flexWrap:'wrap',gap:'6px',padding:'0 16px 12px'}}>
                  {[
                    { key: 'size-asc',  label: 'Size: S → L' },
                    { key: 'size-desc', label: 'Size: L → S' },
                    { key: 'price-asc', label: 'Price: Low → High' },
                    { key: 'dist-asc',  label: 'Nearest first' },
                  ].map(function(opt) {
                    const active = sizeSort === opt.key;
                    return (
                      <button
                        key={opt.key}
                        onClick={function() { setSizeSort(opt.key); }}
                        style={{
                          fontSize:'12px', fontWeight:'600', padding:'5px 11px',
                          border: active ? '1.5px solid #1A7A4A' : '1.5px solid #d1d5db',
                          borderRadius:'20px',
                          background: active ? '#e6f4ec' : 'white',
                          color: active ? '#1A7A4A' : '#374151',
                          cursor:'pointer', whiteSpace:'nowrap',
                        }}
                      >{opt.label}</button>
                    );
                  })}
                </div>
                <div className="cards-grid">
                  {[...sizeSummaries].sort(function(a, b) {
                    if (sizeSort === 'size-desc') return sizeSummaries.indexOf(b) - sizeSummaries.indexOf(a);
                    if (sizeSort === 'price-asc') return a['price_min_' + duration] - b['price_min_' + duration];
                    if (sizeSort === 'dist-asc') {
                      const da = a.min_distance_miles != null ? a.min_distance_miles : Infinity;
                      const db = b.min_distance_miles != null ? b.min_distance_miles : Infinity;
                      return da - db;
                    }
                    return 0; // size-asc: preserve backend order (7yd → … → 40yd)
                  }).map(function(s) {
                    const minP = s['price_min_' + duration];
                    const maxP = s['price_max_' + duration];
                    const priceStr = minP === maxP ? '$' + minP : '$' + minP + '–$' + maxP;
                    return (
                      <div
                        key={s.size}
                        className="dumpster-card"
                        style={{cursor:'pointer'}}
                        onClick={function() { loadHaulersForSize(s.size); }}
                      >
                        <div className="d-icon"><RollOffDumpsterSVG size={56} /></div>
                        <div className="d-info">
                          <div className="d-size">{s.label} Dumpster</div>
                          <div className="d-desc">{s.desc}</div>
                          {s.concrete_only && (
                            <div style={{display:'inline-block',fontSize:'10px',fontWeight:'700',color:'#92400e',background:'#fef3c7',border:'1px solid #fcd34d',borderRadius:'4px',padding:'1px 6px',marginTop:'3px',marginBottom:'2px'}}>
                              Concrete / Heavy Debris Only
                            </div>
                          )}
                          {s.size === preferredSize && (
                            <div style={{display:'inline-block',fontSize:'10px',fontWeight:'700',color:'white',background:'#1A7A4A',borderRadius:'4px',padding:'2px 7px',marginTop:'2px',marginBottom:'2px'}}>
                              ✓ Recommended by Estimator
                            </div>
                          )}
                          <span className="avail">{s.total_quantity} available</span>
                        </div>
                        <div className="d-price">
                          <div className="price">{priceStr}</div>
                          <div className="price-sub">{duration.replace('day', ' days')}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {searchStep === 'haulers' && (
              <>
                <button
                  onClick={function() { setSearchStep('sizes'); setActiveHauler(null); }}
                  style={{background:'none',border:'none',color:'#1A7A4A',cursor:'pointer',fontSize:'13px',fontWeight:'600',padding:'0 0 14px',display:'block'}}
                >
                  ← Back to sizes
                </button>
                <div className="results-header">
                  <div>
                    <h2>{selectedSizeSummary ? selectedSizeSummary.label : selectedSize} Dumpsters near {displayLocation}</h2>
                    <p>{haulerLoading ? 'Loading…' : (haulerResults.length + ' available')}</p>
                  </div>
                  {durTabs}
                </div>
                {haulerLoading ? (
                  <div style={{textAlign:'center',padding:'40px 20px',color:'#6b7280'}}>Loading dumpsters…</div>
                ) : haulerResults.length === 0 ? (
                  <div style={{textAlign:'center',padding:'40px 20px',color:'#6b7280'}}>No dumpsters available for this size right now.</div>
                ) : (
                  haulerResults.map(function(item) {
                    const isHighlighted = item.hauler_id === activeHauler;
                    return (
                      <div
                        className="dumpster-card"
                        key={item.hauler_id + '_' + item.size}
                        onClick={function() { onSelect(item); }}
                        style={{cursor:'pointer', outline: isHighlighted ? '2px solid #1A7A4A' : 'none', outlineOffset: '-2px'}}
                      >
                        <div className="d-icon"><RollOffDumpsterSVG size={56} /></div>
                        <div className="d-info">
                          <div className="d-size">{item.label} Dumpster</div>
                          <div className="d-desc">{item.desc}</div>
                          <span className="avail">
                            Available{item.distance_miles != null ? ' · ' + item.distance_miles + ' mi away' : ''}
                          </span>
                          {item.included_tonnage > 0 && (
                            <div style={{fontSize:'11px',color:'#1A7A4A',fontWeight:'600',marginTop:'4px'}}>
                              Includes {item.included_tonnage} tons (per hauler terms)
                            </div>
                          )}
                          {(item.daily_rate > 0 || item.overage_per_ton > 0) && (
                            <div style={{fontSize:'11px',color:'#6B6B6B',marginTop:'2px',lineHeight:'1.35'}}>
                              {[
                                item.daily_rate > 0 ? '+$' + item.daily_rate + '/extra day' : null,
                                item.overage_per_ton > 0 ? '+$' + item.overage_per_ton + '/ton over' : null,
                              ].filter(Boolean).join(' · ')}
                            </div>
                          )}
                        </div>
                        <div className="d-price">
                          <div className="price">{'$' + getPrice(item, duration)}</div>
                          <div className="price-sub">{duration.replace('day', ' days')}</div>
                        </div>
                      </div>
                    );
                  })
                )}
              </>
            )}
          </div>
        </div>
      )}

      {!loading && !searchStep && (
        <EstimatorSection zip={searchZip || ''} onBook={function(sizeYd, address) {
          setPreferredSize(sizeYd + 'yd');
          if (address && inputRef.current) {
            inputRef.current.value = address;
            handleSearch();
          } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            setTimeout(function() { if (inputRef.current) inputRef.current.focus(); }, 400);
          }
        }} />
      )}

      {/* How it works — only shown on the landing page before a search */}
      {!loading && !searchStep && (
        <section style={{width:'100%',background:'#fff',fontFamily:'sans-serif'}}>
          <div style={{maxWidth:'960px',margin:'0 auto',padding:'72px 24px'}}>
            <h2 style={{fontSize:'clamp(1.5rem,4vw,2rem)',fontWeight:'900',color:'#0D0D0D',textAlign:'center',margin:'0 0 52px',letterSpacing:'-0.5px'}}>
              How it works
            </h2>
            <div style={{display:'flex',flexWrap:'wrap',gap:'40px',justifyContent:'center'}}>

              <div style={{flex:'1 1 220px',minWidth:'0',display:'flex',flexDirection:'column',alignItems:'flex-start'}}>
                <div style={{width:'48px',height:'48px',background:'#111',borderRadius:'12px',display:'flex',alignItems:'center',justifyContent:'center',color:'#1D9E75',marginBottom:'20px',flexShrink:0}}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                </div>
                <div style={{fontSize:'11px',fontWeight:'700',color:'#1D9E75',letterSpacing:'1px',textTransform:'uppercase',marginBottom:'8px'}}>01</div>
                <div style={{fontSize:'18px',fontWeight:'900',color:'#0D0D0D',marginBottom:'10px',letterSpacing:'-0.3px'}}>Search</div>
                <div style={{fontSize:'14px',color:'#666',lineHeight:'1.65'}}>Enter your address and see dumpsters available near you.</div>
              </div>

              <div style={{flex:'1 1 220px',minWidth:'0',display:'flex',flexDirection:'column',alignItems:'flex-start'}}>
                <div style={{width:'48px',height:'48px',background:'#111',borderRadius:'12px',display:'flex',alignItems:'center',justifyContent:'center',color:'#1D9E75',marginBottom:'20px',flexShrink:0}}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                </div>
                <div style={{fontSize:'11px',fontWeight:'700',color:'#1D9E75',letterSpacing:'1px',textTransform:'uppercase',marginBottom:'8px'}}>02</div>
                <div style={{fontSize:'18px',fontWeight:'900',color:'#0D0D0D',marginBottom:'10px',letterSpacing:'-0.3px'}}>Book</div>
                <div style={{fontSize:'14px',color:'#666',lineHeight:'1.65'}}>Pick your size and hauler, then pay securely online.</div>
              </div>

              <div style={{flex:'1 1 220px',minWidth:'0',display:'flex',flexDirection:'column',alignItems:'flex-start'}}>
                <div style={{width:'48px',height:'48px',background:'#111',borderRadius:'12px',display:'flex',alignItems:'center',justifyContent:'center',color:'#1D9E75',marginBottom:'20px',flexShrink:0}}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>
                  </svg>
                </div>
                <div style={{fontSize:'11px',fontWeight:'700',color:'#1D9E75',letterSpacing:'1px',textTransform:'uppercase',marginBottom:'8px'}}>03</div>
                <div style={{fontSize:'18px',fontWeight:'900',color:'#0D0D0D',marginBottom:'10px',letterSpacing:'-0.3px'}}>Delivered</div>
                <div style={{fontSize:'14px',color:'#666',lineHeight:'1.65'}}>A local hauler delivers to your job site. That&apos;s it.</div>
              </div>

            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer style={{width:'100%',background:'#111',color:'white',padding:'56px 24px 32px',marginTop:'auto',fontFamily:'sans-serif'}}>
        <div style={{maxWidth:'960px',margin:'0 auto'}}>
          <div style={{display:'flex',flexWrap:'wrap',gap:'40px 48px',marginBottom:'48px'}}>

            {/* Brand */}
            <div style={{flex:'1 1 240px',minWidth:'0'}}>
              <div onClick={function(){window.location.href='/';}} style={{fontSize:'26px',fontWeight:'900',marginBottom:'12px',cursor:'pointer',letterSpacing:'-0.5px'}}>
                Tam<span style={{color:'#1D9E75'}}>bo</span>
              </div>
              <div style={{fontSize:'11px',fontWeight:'700',color:'rgba(255,255,255,0.4)',letterSpacing:'1.5px',textTransform:'uppercase',marginBottom:'6px'}}>Fast Local Dumpsters</div>
              <div style={{fontSize:'13px',color:'rgba(255,255,255,0.5)',marginBottom:'8px'}}>Serving you locally where you are</div>
              <div style={{fontSize:'12px',color:'rgba(255,255,255,0.3)',lineHeight:'1.55'}}>Serving Texas — expanding nationwide</div>
            </div>

            {/* For Haulers */}
            <div style={{flex:'1 1 140px',minWidth:'0'}}>
              <div style={{fontSize:'11px',fontWeight:'700',color:'rgba(255,255,255,0.35)',letterSpacing:'1px',textTransform:'uppercase',marginBottom:'16px'}}>For Haulers</div>
              <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
                <a href="/hauler-signup" style={{color:'rgba(255,255,255,0.7)',textDecoration:'none',fontSize:'14px'}}>Become a Hauler</a>
                <a href="/hauler-login" style={{color:'rgba(255,255,255,0.7)',textDecoration:'none',fontSize:'14px'}}>Hauler Sign In</a>
              </div>
            </div>

            {/* For Clients */}
            <div style={{flex:'1 1 140px',minWidth:'0'}}>
              <div style={{fontSize:'11px',fontWeight:'700',color:'rgba(255,255,255,0.35)',letterSpacing:'1px',textTransform:'uppercase',marginBottom:'16px'}}>For Clients</div>
              <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
                <a href="/signup" style={{color:'rgba(255,255,255,0.7)',textDecoration:'none',fontSize:'14px'}}>Client Sign Up</a>
                <a href="/login" style={{color:'rgba(255,255,255,0.7)',textDecoration:'none',fontSize:'14px'}}>Client Log In</a>
              </div>
            </div>

            {/* Legal & Contact */}
            <div style={{flex:'1 1 160px',minWidth:'0'}}>
              <div style={{fontSize:'11px',fontWeight:'700',color:'rgba(255,255,255,0.35)',letterSpacing:'1px',textTransform:'uppercase',marginBottom:'16px'}}>Legal &amp; Contact</div>
              <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
                <a href="/privacy" style={{color:'rgba(255,255,255,0.7)',textDecoration:'none',fontSize:'14px'}}>Privacy Policy</a>
                <a href="/terms" style={{color:'rgba(255,255,255,0.7)',textDecoration:'none',fontSize:'14px'}}>Terms of Service</a>
                <a href="/sms-opt-in" style={{color:'rgba(255,255,255,0.7)',textDecoration:'none',fontSize:'14px'}}>SMS Consent</a>
                <a href="tel:9562263903" style={{color:'rgba(255,255,255,0.7)',textDecoration:'none',fontSize:'14px'}}>(956) 226-3903</a>
                <a href="mailto:office@tambodumpsters.com" style={{color:'rgba(255,255,255,0.7)',textDecoration:'none',fontSize:'14px'}}>office@tambodumpsters.com</a>
              </div>
            </div>

          </div>
          <div style={{borderTop:'1px solid rgba(255,255,255,0.08)',paddingTop:'24px'}}>
            <div style={{fontSize:'12px',color:'#444'}}>© 2026 Tambo Technologies LLC</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function DetailPage({ item, duration, setDuration, onBack, onBook, envFee }) {
  return (
    <div className="detail">
      <div className="detail-hero">
        <button className="back" onClick={onBack}>Back</button>
        <h2>{item.label} Dumpster</h2>
        <p>{item.desc}</p>
      </div>
      <div className="detail-body">
        <div className="section-label">Rental length</div>
        <div className="dur-tabs big">
          <button className={duration === '3day' ? 'active' : ''} onClick={function() { setDuration('3day'); }}>
            <div className="dur-days">3 days</div>
            <div className="dur-price">${item.price_3day || item.p3}</div>
          </button>
          <button className={duration === '5day' ? 'active' : ''} onClick={function() { setDuration('5day'); }}>
            <div className="dur-days">5 days</div>
            <div className="dur-price">${item.price_5day || item.p5}</div>
          </button>
          <button className={duration === '7day' ? 'active' : ''} onClick={function() { setDuration('7day'); }}>
            <div className="dur-days">7 days</div>
            <div className="dur-price">${item.price_7day || item.p7}</div>
          </button>
        </div>
        {item.included_tonnage > 0 && (
          <div className="extra-note" style={{marginBottom:'6px',background:'#E8F5EE',color:'#155731',fontWeight:'600'}}>
            Includes {item.included_tonnage} tons (per hauler terms)
            {item.overage_per_ton > 0 ? ` · +$${item.overage_per_ton}/ton over limit` : ''}
          </div>
        )}
        {(item.daily_rate > 0 || (!item.included_tonnage && item.overage_per_ton > 0)) && (
          <div className="extra-note" style={{marginBottom:'6px'}}>
            {'Includes ' + (item.included_days || 3) + ' days.'}
            {item.daily_rate > 0 ? ' +$' + item.daily_rate + '/extra day.' : ''}
            {item.overage_per_ton > 0 && !item.included_tonnage ? ' +$' + item.overage_per_ton + '/ton over.' : ''}
          </div>
        )}
        <div className="extra-note">Need longer? +${item.extra_day || item.extra}/day for each extra day.</div>
        <div className="section-label" style={{marginTop:20}}>Price summary</div>
        <div className="price-box">
          <div className="price-row"><span>{item.label} rental</span><span>${getPrice(item, duration)}</span></div>
          <div className="price-row"><span>Delivery and pickup</span><span>Included</span></div>
          {(envFee > 0) && <div className="price-row"><span>Environmental fee</span><span>${envFee.toFixed(2)}</span></div>}
          <div className="price-row"><span>Subtotal</span><span>${(getPrice(item, duration) + (envFee || 0)).toFixed(2)}</span></div>
          <div className="price-row"><span>Sales Tax (8.25%)</span><span>${calcTotals(getPrice(item, duration), envFee).tax.toFixed(2)}</span></div>
          <div className="price-row total"><span>Total</span><span>${calcTotals(getPrice(item, duration), envFee).total.toFixed(2)}</span></div>
        </div>
        <button className="book-btn" onClick={onBook}>Confirm and Pay</button>
      </div>
    </div>
  );
}

function ConfirmPage({ item, duration, onReset, orderNumber, guestInfo, deliverySchedule, isPayLater, envFee }) {
  return (
    <div className="confirm">
      <div className="confirm-hero">
        <div className="logo" onClick={function(){window.location.href='/';}} style={{cursor:'pointer'}}>Tam<span>bo</span></div>
      </div>
      <div className="confirm-body">
        <div className="confirm-icon">🎉</div>
        <h2>{isPayLater ? 'Order Placed!' : 'Order Confirmed!'}</h2>
        <p>{isPayLater ? 'This will be charged to your card on file when delivered.' : 'Your dumpster is booked and on its way.'}</p>
        {guestInfo && (
          <div style={{background:'#E8F5EE',borderRadius:'10px',padding:'12px',marginBottom:'16px',fontSize:'13px',textAlign:'left'}}>
            <div style={{fontWeight:'700',marginBottom:'4px',color:'#1A7A4A'}}>Confirmation sent to:</div>
            <div>{guestInfo.name} · {guestInfo.email}</div>
            <div>{guestInfo.phone}</div>
            <div style={{marginTop:'4px',color:'#6B6B6B'}}>{guestInfo.serviceAddress}</div>
          </div>
        )}
        <div className="confirm-card">
          <div className="confirm-row"><span>Order</span><span>{orderNumber}</span></div>
          <div className="confirm-row"><span>Size</span><span>{item.label}</span></div>
          <div className="confirm-row"><span>Duration</span><span>{duration.replace('day', ' days')}</span></div>
          <div className="confirm-row"><span>Waste type</span><span>{guestInfo ? guestInfo.wasteType : 'General'}</span></div>
          <div className="confirm-row"><span>Delivery</span><span>{deliverySchedule ? deliverySchedule.displayLabel : 'Scheduled'}</span></div>
          <div className="confirm-row"><span>Total paid</span><span>${calcTotals(getPrice(item, duration), envFee).total.toFixed(2)}</span></div>
        </div>
        <div className="status-bar">
          <div className="status-step done">Confirmed</div>
          <div className="status-line"></div>
          <div className="status-step active">Dispatching</div>
          <div className="status-line"></div>
          <div className="status-step">Delivered</div>
        </div>
        <p className="sms-note">You will get a text when your hauler is assigned!</p>
        <button className="book-btn" onClick={onReset}>Book another</button>
      </div>
    </div>
  );
}

const TIME_WINDOWS = [
  { id: 'morning',   label: 'Morning',   time: '8am – 12pm', start: '08:00', end: '12:00' },
  { id: 'afternoon', label: 'Afternoon', time: '12pm – 4pm', start: '12:00', end: '16:00' },
  { id: 'evening',   label: 'Evening',   time: '4pm – 7pm',  start: '16:00', end: '19:00' },
];

function getAvailableDates() {
  const dates = [];
  const d = new Date();
  d.setDate(d.getDate() + 1);
  while (dates.length < 7) {
    if (d.getDay() !== 0) dates.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return dates;
}

function toLocalISODate(d) {
  return d.getFullYear() + '-' +
    String(d.getMonth() + 1).padStart(2, '0') + '-' +
    String(d.getDate()).padStart(2, '0');
}

function DeliverySchedulePage({ item, duration, total, onBack, onNext, serviceLatLng, serviceAddress }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedWindow, setSelectedWindow] = useState(null);
  const [error, setError] = useState('');
  const [specialInstructions, setSpecialInstructions] = useState('');

  // Start with whatever lat/lng we already have (may be null if hero search was ZIP-only)
  const hasLatLng = !!(serviceLatLng?.lat && serviceLatLng?.lng);
  const [placementLat, setPlacementLat] = useState(hasLatLng ? serviceLatLng.lat : null);
  const [placementLng, setPlacementLng] = useState(hasLatLng ? serviceLatLng.lng : null);
  const [geoLoading, setGeoLoading] = useState(!hasLatLng); // true when we still need to geocode
  const [mapReady, setMapReady] = useState(!!(window.google && window.google.maps));
  const mapElRef = useRef(null);
  const markerRef = useRef(null);
  const mapInstanceRef = useRef(null);

  console.log('[DeliverySchedulePage] serviceLatLng:', serviceLatLng, '| serviceAddress:', serviceAddress, '| hasLatLng:', hasLatLng);

  const dates = getAvailableDates();

  const labelStyle = {fontSize:'11px',fontWeight:'700',color:'#6B6B6B',textTransform:'uppercase',letterSpacing:'.5px',display:'block',marginBottom:'10px'};

  // 1. Poll until Google Maps JS is loaded
  useEffect(function() {
    if (mapReady) return;
    var timer = setInterval(function() {
      if (window.google && window.google.maps) { setMapReady(true); clearInterval(timer); }
    }, 200);
    return function() { clearInterval(timer); };
  }, [mapReady]);

  // 2. Once Maps is loaded, geocode the service address if we don't have coordinates yet
  useEffect(function() {
    if (!geoLoading) return;   // already have coords
    if (!mapReady) return;     // wait for Maps SDK

    var addr = serviceAddress || serviceLatLng?.fullAddress || '';
    console.log('[DeliverySchedulePage] geocoding:', addr);
    if (!addr) { setGeoLoading(false); return; }

    var geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: addr }, function(results, status) {
      if (status === 'OK' && results[0]?.geometry?.location) {
        var lat = results[0].geometry.location.lat();
        var lng = results[0].geometry.location.lng();
        console.log('[DeliverySchedulePage] geocoded to', lat, lng);
        setPlacementLat(lat);
        setPlacementLng(lng);
      } else {
        console.warn('[DeliverySchedulePage] geocode failed:', status);
      }
      setGeoLoading(false);
    });
  }, [mapReady, geoLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // 3. Initialize the map once Maps is ready and coords are resolved
  useEffect(function() {
    if (!mapReady || !mapElRef.current || geoLoading) return;
    if (mapInstanceRef.current) return; // already initialized

    var lat = placementLat || 26.2034;
    var lng = placementLng || -98.2300;
    var map = new window.google.maps.Map(mapElRef.current, {
      center: { lat: lat, lng: lng },
      zoom: 20,
      mapTypeId: 'satellite',
      disableDefaultUI: true,
      zoomControl: true,
    });
    mapInstanceRef.current = map;
    var marker = new window.google.maps.Marker({
      position: { lat: lat, lng: lng },
      map: map,
      draggable: true,
      title: 'Drag to exact placement spot',
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 12,
        fillColor: '#1A7A4A',
        fillOpacity: 1,
        strokeColor: '#fff',
        strokeWeight: 2,
      },
    });
    marker.addListener('dragend', function(evt) {
      setPlacementLat(evt.latLng.lat());
      setPlacementLng(evt.latLng.lng());
    });
    markerRef.current = marker;
  }, [mapReady, geoLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // 4. Re-center when coords update (e.g. after geocoding completes while map already existed)
  useEffect(function() {
    if (!mapInstanceRef.current || !markerRef.current || !placementLat || !placementLng) return;
    var pos = { lat: placementLat, lng: placementLng };
    mapInstanceRef.current.setCenter(pos);
    markerRef.current.setPosition(pos);
  }, [placementLat, placementLng]);

  const formatDayLabel = function(d) {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (d.toDateString() === tomorrow.toDateString()) return 'Tomorrow';
    return d.toLocaleDateString('en-US', { weekday: 'short' });
  };

  const handleNext = function() {
    if (!selectedDate) { setError('Please select a delivery date'); return; }
    if (!selectedWindow) { setError('Please select a time window'); return; }
    const win = TIME_WINDOWS.find(function(w) { return w.id === selectedWindow; });
    const shortDate = selectedDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    onNext({
      date: toLocalISODate(selectedDate),
      window: selectedWindow,
      windowStart: win.start,
      windowEnd: win.end,
      displayLabel: shortDate + ' · ' + win.time,
      specialInstructions: specialInstructions.trim() || null,
      placementLat: placementLat,
      placementLng: placementLng,
    });
  };

  return (
    <div style={{minHeight:'100vh',background:'#EDEDEA'}}>
      <div style={{background:'#0D0D0D',padding:'16px 24px'}}>
        <div onClick={function(){window.location.href='/';}} style={{fontFamily:'sans-serif',fontSize:'22px',fontWeight:'800',color:'white',cursor:'pointer'}}>
          Tambo
        </div>
      </div>
      <div style={{background:'#1A7A4A',padding:'16px 24px',color:'white'}}>
        <button onClick={onBack} style={{background:'none',border:'none',color:'white',opacity:.7,fontSize:'13px',cursor:'pointer',marginBottom:'8px',display:'block'}}>← Back</button>
        <div style={{fontFamily:'sans-serif',fontSize:'18px',fontWeight:'800'}}>{item.label} Dumpster</div>
        <div style={{fontSize:'13px',opacity:.8,marginTop:'4px'}}>Total: ${total}</div>
      </div>
      <div style={{padding:'20px',maxWidth:'480px',margin:'0 auto'}}>
        <div style={{fontFamily:'sans-serif',fontSize:'18px',fontWeight:'800',marginBottom:'4px'}}>Choose your delivery</div>
        <div style={{fontSize:'13px',color:'#6B6B6B',marginBottom:'24px'}}>Pick the date and arrival window that works best.</div>

        <label style={labelStyle}>Delivery Date</label>
        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px',marginBottom:'28px'}}>
          {dates.map(function(d) {
            const iso = toLocalISODate(d);
            const active = selectedDate && toLocalISODate(selectedDate) === iso;
            return (
              <button key={iso} onClick={function() { setSelectedDate(d); setError(''); }}
                style={{padding:'10px 4px',border:active?'2px solid #1A7A4A':'1.5px solid #E0E0DA',borderRadius:'10px',
                  background:active?'#E8F5EE':'white',cursor:'pointer',textAlign:'center'}}>
                <div style={{fontSize:'10px',color:active?'#1A7A4A':'#6B6B6B',fontWeight:'700',marginBottom:'3px'}}>
                  {formatDayLabel(d)}
                </div>
                <div style={{fontSize:'13px',fontWeight:'700',color:active?'#1A7A4A':'#0D0D0D'}}>
                  {d.toLocaleDateString('en-US',{month:'short',day:'numeric'})}
                </div>
              </button>
            );
          })}
        </div>

        <label style={labelStyle}>Delivery Window</label>
        <div style={{display:'flex',flexDirection:'column',gap:'10px',marginBottom:'24px'}}>
          {TIME_WINDOWS.map(function(w) {
            const active = selectedWindow === w.id;
            return (
              <button key={w.id} onClick={function() { setSelectedWindow(w.id); setError(''); }}
                style={{display:'flex',alignItems:'center',justifyContent:'space-between',
                  padding:'14px 16px',border:active?'2px solid #1A7A4A':'1.5px solid #E0E0DA',
                  borderRadius:'12px',background:active?'#E8F5EE':'white',cursor:'pointer',textAlign:'left',
                  outline:'none',WebkitAppearance:'none',appearance:'none',width:'100%',fontFamily:'inherit'}}>
                <div>
                  <div style={{fontWeight:'700',fontSize:'14px',color:active?'#1A7A4A':'#0D0D0D'}}>{w.label}</div>
                  <div style={{fontSize:'13px',color:'#6B6B6B',marginTop:'2px'}}>{w.time}</div>
                </div>
                <div style={{width:'20px',height:'20px',borderRadius:'50%',flexShrink:0,boxSizing:'border-box',
                  border:active?'6px solid #1A7A4A':'2px solid #D0D0CA',backgroundColor:'white'}}/>
              </button>
            );
          })}
        </div>

        {/* Special instructions */}
        <label style={labelStyle}>Special instructions (optional)</label>
        <textarea
          style={{width:'100%',padding:'12px',border:'1.5px solid #E0E0DA',borderRadius:'10px',
            fontSize:'13px',fontFamily:'inherit',resize:'vertical',minHeight:'80px',
            boxSizing:'border-box',marginBottom:'24px',color:'#0D0D0D',outline:'none'}}
          placeholder={'Need it by a specific time? Gate codes, placement notes, anything the driver should know.'}
          maxLength={500}
          value={specialInstructions}
          onChange={function(e) { setSpecialInstructions(e.target.value); }}
        />

        {/* Dumpster placement map */}
        <label style={labelStyle}>Where should we drop it?</label>
        <div style={{marginBottom:'8px',fontSize:'13px',color:'#6B6B6B'}}>
          Drag the pin to exactly where you want the dumpster.
        </div>
        {(mapReady && !geoLoading) ? (
          <div ref={mapElRef} style={{width:'100%',height:'260px',borderRadius:'12px',marginBottom:'24px',overflow:'hidden',border:'1.5px solid #E0E0DA'}} />
        ) : (
          <div style={{width:'100%',height:'260px',borderRadius:'12px',marginBottom:'24px',background:'#F3F4F0',
            display:'flex',alignItems:'center',justifyContent:'center',fontSize:'13px',color:'#9CA3AF',
            border:'1.5px solid #E0E0DA'}}>
            {geoLoading ? 'Locating address…' : 'Loading map…'}
          </div>
        )}

        {error && <div style={{background:'#FEE2E2',color:'#DC2626',borderRadius:'8px',padding:'10px',marginBottom:'12px',fontSize:'13px'}}>{error}</div>}

        <button onClick={handleNext}
          style={{width:'100%',padding:'14px',background:'#0D0D0D',color:'white',border:'none',
            borderRadius:'10px',fontSize:'15px',fontWeight:'700',cursor:'pointer',marginBottom:'10px'}}>
          Continue to Payment
        </button>
        <button onClick={onBack}
          style={{width:'100%',padding:'13px',background:'white',color:'#6B6B6B',
            border:'1.5px solid #E0E0DA',borderRadius:'10px',fontSize:'14px',cursor:'pointer'}}>
          Back
        </button>
      </div>
    </div>
  );
}

function App() {
  const [selected, setSelected] = useState(null);
  const [duration, setDuration] = useState('5day');
  const [booked, setBooked] = useState(false);
  const [paying, setPaying] = useState(false);
  const [collectingInfo, setCollectingInfo] = useState(false);
  const [collectingSchedule, setCollectingSchedule] = useState(false);
  const [guestInfo, setGuestInfo] = useState(null);
  const [deliverySchedule, setDeliverySchedule] = useState(null);
  const [user, setUser] = useState(null);
  const [orderNumber, setOrderNumber] = useState('');
  const [searchZip, setSearchZip] = useState('');
  const [searchAddress, setSearchAddress] = useState(null);
  const [prefillData, setPrefillData] = useState(null);
  const [clientProfile, setClientProfile] = useState(null);
  const [isPayLater, setIsPayLater] = useState(false);
  const [savedSearch, setSavedSearch] = useState(null);
  const [payClientSecret,  setPayClientSecret]  = useState(null);
  const [payIntentId,      setPayIntentId]      = useState(null);
  const [payInitLoading,   setPayInitLoading]   = useState(false);
  const [payInitError,     setPayInitError]     = useState('');
  const [envFee,           setEnvFee]           = useState(0);

  useEffect(function() {
    supabase.auth.getSession().then(function(result) {
      if (result.data.session) setUser(result.data.session.user);
    });
  }, []);

  // Fetch a PaymentIntent as soon as the client reaches the payment step so the
  // PaymentElement can mount with a clientSecret before the user clicks Pay.
  useEffect(function() {
    if (!paying || !selected || payClientSecret) return;
    setPayInitLoading(true);
    setPayInitError('');
    var price = getPrice(selected, duration);
    var total = calcTotals(price, envFee).total;
    fetch(BACKEND + '/api/orders/payment-intent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ total_charged: total, user_id: user ? user.id : null }),
    })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.error) throw new Error(d.error);
        setPayClientSecret(d.client_secret);
        setPayIntentId(d.payment_intent_id);
      })
      .catch(function(err) { setPayInitError(err.message || 'Could not start checkout'); })
      .finally(function() { setPayInitLoading(false); });
  }, [paying]); // eslint-disable-line react-hooks/exhaustive-deps

  function reset() {
    setSelected(null);
    setBooked(false);
    setPaying(false);
    setCollectingInfo(false);
    setCollectingSchedule(false);
    setGuestInfo(null);
    setDeliverySchedule(null);
    setDuration('5day');
    setOrderNumber('');
    setSearchZip('');
    setSearchAddress(null);
    setPrefillData(null);
    setClientProfile(null);
    setIsPayLater(false);
    setPayClientSecret(null);
    setPayIntentId(null);
    setPayInitLoading(false);
    setPayInitError('');
  }

  if (booked && selected) {
    return <ConfirmPage item={selected} duration={duration} onReset={reset} orderNumber={orderNumber} guestInfo={guestInfo} deliverySchedule={deliverySchedule} isPayLater={isPayLater} envFee={envFee} />;
  }

  if (collectingInfo && selected) {
    const price = getPrice(selected, duration);
    const total = calcTotals(price, envFee).total;
    return (
      <GuestInfoForm
        total={total}
        item={selected}
        duration={duration}
        initialData={prefillData}
        onCancel={function() { setCollectingInfo(false); setPrefillData(null); }}
        onNext={function(info) {
          setGuestInfo(info);
          setCollectingInfo(false);
          setCollectingSchedule(true);
        }}
      />
    );
  }

  if (collectingSchedule && selected) {
    const price = getPrice(selected, duration);
    const total = calcTotals(price, envFee).total;
    return (
      <DeliverySchedulePage
        item={selected}
        duration={duration}
        total={total}
        serviceLatLng={searchAddress || null}
        serviceAddress={guestInfo ? guestInfo.serviceAddress : ''}
        onBack={function() {
          setCollectingSchedule(false);
          if (guestInfo) setCollectingInfo(true);
        }}
        onNext={function(schedule) {
          setDeliverySchedule(schedule);
          setCollectingSchedule(false);
          setPaying(true);
        }}
      />
    );
  }

  if (paying && selected) {
    const price = getPrice(selected, duration);
    const { subtotal, tax, total } = calcTotals(price, envFee);
    const orderData = {
      user_id: user ? user.id : null,
      guest_name: guestInfo ? guestInfo.name : null,
      guest_email: guestInfo ? guestInfo.email : null,
      guest_phone: guestInfo ? guestInfo.phone : null,
      guest_company: guestInfo ? guestInfo.company : null,
      guest_address: guestInfo ? guestInfo.serviceAddress : null,
      billing_address: guestInfo ? guestInfo.billingAddress : null,
      billing_city: guestInfo ? guestInfo.billingCity : null,
      billing_state: guestInfo ? guestInfo.billingState : null,
      billing_zip: guestInfo ? guestInfo.billingZip : null,
      waste_type: guestInfo ? guestInfo.wasteType : null,
      delivery_zip: guestInfo ? guestInfo.deliveryZip : searchZip,
      size: selected.size,
      hauler_id: selected.hauler_id,
      duration: duration.replace('day', '_day'),
      client_price: price,
      tax_amount: tax,
      total_charged: total,
      delivery_date: deliverySchedule ? deliverySchedule.date : null,
      delivery_window: deliverySchedule ? deliverySchedule.window : null,
      sms_consent: guestInfo ? (guestInfo.smsConsent || false) : false,
      sms_consent_at: guestInfo ? (guestInfo.smsConsentAt || null) : null,
      lat: searchAddress ? searchAddress.lat : null,
      lng: searchAddress ? searchAddress.lng : null,
      special_instructions: deliverySchedule ? (deliverySchedule.specialInstructions || null) : null,
      placement_lat: deliverySchedule ? (deliverySchedule.placementLat || null) : null,
      placement_lng: deliverySchedule ? (deliverySchedule.placementLng || null) : null,
    };
    const onSuccess = function(order) {
      setOrderNumber(order.order_number);
      setPayClientSecret(null); setPayIntentId(null);
      setPaying(false); setBooked(true);
    };
    const onPayLaterSuccess = function(order) {
      setOrderNumber(order.order_number);
      setIsPayLater(true);
      setPayClientSecret(null); setPayIntentId(null);
      setPaying(false); setBooked(true);
    };
    const onCancel = function() {
      setPayClientSecret(null); setPayIntentId(null);
      setPayInitError(''); setPaying(false);
    };
    return (
      <div style={{minHeight:'100vh',background:'#EDEDEA'}}>
        <div style={{background:'#0D0D0D',padding:'16px 24px'}}>
          <div style={{fontFamily:'sans-serif',fontSize:'22px',fontWeight:'800',color:'white'}}>
            Tambo
          </div>
        </div>
        {payInitLoading && (
          <div style={{textAlign:'center',padding:'60px 20px',color:'#6b7280',fontSize:'14px'}}>
            Preparing secure checkout…
          </div>
        )}
        {payInitError && !payInitLoading && (
          <div style={{maxWidth:'480px',margin:'40px auto',padding:'0 20px'}}>
            <div style={{background:'#FEE2E2',color:'#DC2626',borderRadius:'8px',padding:'12px',marginBottom:'12px',fontSize:'13px'}}>{payInitError}</div>
            <button onClick={onCancel} style={{width:'100%',padding:'13px',background:'white',color:'#6B6B6B',border:'1.5px solid #E0E0DA',borderRadius:'10px',fontSize:'14px',cursor:'pointer'}}>Go Back</button>
          </div>
        )}
        {payClientSecret && !payInitLoading && (
          <Elements stripe={stripePromise} options={{ clientSecret: payClientSecret, appearance: { theme: 'stripe' } }}>
            <PaymentForm
              amount={total}
              subtotal={subtotal}
              tax={tax}
              clientProfile={clientProfile}
              paymentIntentId={payIntentId}
              onCancel={onCancel}
              onSuccess={onSuccess}
              onPayLaterSuccess={onPayLaterSuccess}
              orderData={orderData}
            />
          </Elements>
        )}
      </div>
    );
  }

  if (selected) {
    return (
      <DetailPage
        item={selected}
        duration={duration}
        setDuration={setDuration}
        envFee={envFee}
        onBack={function() { setSelected(null); }}
        onBook={async function() {
          const parseAddr = function(addr) {
            if (!addr) return { streetAddress: '', city: '', stateAbbr: '', zip: '' };
            const parts = addr.split(',').map(function(s) { return s.trim(); });
            if (parts.length >= 3) {
              const sv = parts[parts.length - 1].split(' ').filter(Boolean);
              return {
                streetAddress: parts.slice(0, parts.length - 2).join(', '),
                city: parts[parts.length - 2] || '',
                stateAbbr: sv[0] || '',
                zip: sv[1] || '',
              };
            }
            return { streetAddress: addr, city: '', stateAbbr: '', zip: '' };
          };
          const addrPrefill = {
            streetAddress: searchAddress?.streetAddress || '',
            city:          searchAddress?.city || '',
            stateAbbr:     searchAddress?.stateAbbr || '',
            zip:           searchZip || '',
          };
          if (user) {
            try {
              const res = await clientFetch(BACKEND + '/api/clients/me');
              const data = await res.json();
              if (data.profile) {
                const p = data.profile;
                setClientProfile(p);
                const fullName = [p.first_name, p.last_name].filter(Boolean).join(' ');
                const parsed = p.billing_address ? parseAddr(p.billing_address) : {};
                setPrefillData({
                  name: fullName,
                  email: user.email || '',
                  phone: p.phone || '',
                  company: p.company_name || '',
                  // Search address takes priority — it's where the client wants the dumpster delivered.
                  // Billing address is only a last-resort fallback.
                  streetAddress: addrPrefill.streetAddress || parsed.streetAddress || '',
                  city:          addrPrefill.city          || parsed.city          || '',
                  stateAbbr:     addrPrefill.stateAbbr     || parsed.stateAbbr     || '',
                  zip:           addrPrefill.zip            || parsed.zip            || '',
                });
              } else {
                setClientProfile(null);
                setPrefillData(addrPrefill);
              }
            } catch (e) {
              setClientProfile(null);
              setPrefillData(addrPrefill);
            }
          } else {
            setClientProfile(null);
            setPrefillData(addrPrefill);
          }
          setCollectingInfo(true);
        }}
      />
    );
  }

  return (
    <SearchPage
      user={user}
      onSelect={setSelected}
      duration={duration}
      setDuration={setDuration}
      savedSearch={savedSearch}
      onSaveSearch={setSavedSearch}
      onEnvFeeChange={setEnvFee}
      onSearch={function({ zip, fullAddress, streetAddress, city, stateAbbr, lat, lng }) {
        setSearchZip(zip || '');
        setSearchAddress({ fullAddress: fullAddress || '', streetAddress: streetAddress || '', city: city || '', stateAbbr: stateAbbr || '', lat: lat || null, lng: lng || null });
      }}
    />
  );
}

function OrderRequestModal({ type, order, onClose }) {
  const [reasonType, setReasonType] = useState('Weather');
  const [reason, setReason] = useState('');
  const [proposedDate, setProposedDate] = useState('');
  const [windowStart, setWindowStart] = useState('');
  const [windowEnd, setWindowEnd] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const CANCEL_REASONS = ['Weather', 'Project Delay', 'No Longer Needed', 'Other'];

  const handleSubmit = async function() {
    setSubmitting(true);
    setError('');
    try {
      const endpoint = type === 'cancel' ? '/api/requests/cancel' : '/api/requests/reschedule';
      const body = {
        order_id: order.id,
        reason: type === 'cancel' ? (reasonType === 'Other' ? reason : reasonType) : (reason || null),
      };
      if (type === 'reschedule') {
        if (!proposedDate) { setError('Please select a proposed date.'); setSubmitting(false); return; }
        body.proposed_date = proposedDate;
        body.proposed_window_start = windowStart || null;
        body.proposed_window_end = windowEnd || null;
      }
      const res = await clientFetch(BACKEND + endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Request failed');
      setDone(true);
    } catch (err) {
      if (err && err.message === 'NOT_AUTHENTICATED') {
        setError('Please log in to submit this request.');
      } else {
        setError(err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const overlayStyle = { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000, display:'flex', alignItems:'flex-end', justifyContent:'center', fontFamily:'sans-serif' };
  const sheetStyle = { background:'#fff', borderRadius:'20px 20px 0 0', padding:'20px 20px 40px', width:'100%', maxWidth:'480px', boxSizing:'border-box' };
  const inputStyle = { width:'100%', boxSizing:'border-box', border:'1.5px solid #E0E0DA', borderRadius:'10px', padding:'10px 12px', fontSize:'14px', outline:'none', background:'white', marginBottom:'12px' };
  const labelStyle = { fontSize:'11px', fontWeight:'700', color:'#6B6B6B', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:'5px', display:'block' };

  if (done) {
    const msg = type === 'cancel'
      ? 'Your cancellation request has been sent. Our team will review and contact you shortly.'
      : 'Your reschedule request has been sent. Our team will confirm the new date with you.';
    return (
      <div style={overlayStyle} onClick={onClose}>
        <div style={sheetStyle} onClick={function(e){e.stopPropagation();}}>
          <div style={{textAlign:'center',padding:'16px 0'}}>
            <div style={{fontSize:'32px',marginBottom:'10px'}}>✅</div>
            <div style={{fontSize:'16px',fontWeight:'800',color:'#0D0D0D',marginBottom:'8px'}}>Request Sent!</div>
            <div style={{fontSize:'13px',color:'#6B6B6B',lineHeight:'1.6',marginBottom:'24px'}}>{msg}</div>
            <button onClick={onClose} style={{background:'#1A7A4A',color:'white',border:'none',borderRadius:'10px',padding:'12px 28px',fontSize:'14px',fontWeight:'700',cursor:'pointer'}}>Done</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={sheetStyle} onClick={function(e){e.stopPropagation();}}>
        <div style={{width:'36px',height:'4px',background:'#E0E0DA',borderRadius:'2px',margin:'0 auto 18px'}} />
        <div style={{fontSize:'17px',fontWeight:'800',color:'#0D0D0D',marginBottom:'3px'}}>
          {type === 'cancel' ? 'Request Cancellation' : 'Request Reschedule'}
        </div>
        <div style={{fontSize:'12px',color:'#9CA3AF',marginBottom:'20px'}}>Order #{order.order_number}</div>

        {type === 'cancel' && (
          <>
            <label style={labelStyle}>Reason</label>
            <select value={reasonType} onChange={function(e){setReasonType(e.target.value);}} style={{...inputStyle}}>
              {CANCEL_REASONS.map(function(r) { return <option key={r} value={r}>{r}</option>; })}
            </select>
            {reasonType === 'Other' && (
              <>
                <label style={labelStyle}>Please describe</label>
                <textarea value={reason} onChange={function(e){setReason(e.target.value);}} rows={3}
                  placeholder="Tell us more..." style={{...inputStyle,resize:'vertical'}} />
              </>
            )}
          </>
        )}

        {type === 'reschedule' && (
          <>
            <label style={labelStyle}>Proposed New Date <span style={{color:'#EF4444'}}>*</span></label>
            <input type="date" value={proposedDate} onChange={function(e){setProposedDate(e.target.value);}}
              min={new Date().toISOString().slice(0,10)} style={{...inputStyle}} />
            <label style={labelStyle}>Preferred Time Window (optional)</label>
            <div style={{display:'flex',gap:'8px',marginBottom:'12px'}}>
              <input type="time" value={windowStart} onChange={function(e){setWindowStart(e.target.value);}} style={{...inputStyle,flex:1,marginBottom:0}} />
              <input type="time" value={windowEnd} onChange={function(e){setWindowEnd(e.target.value);}} style={{...inputStyle,flex:1,marginBottom:0}} />
            </div>
            <div style={{marginBottom:'12px'}} />
            <label style={labelStyle}>Reason (optional)</label>
            <input type="text" value={reason} onChange={function(e){setReason(e.target.value);}}
              placeholder="Why do you need to reschedule?" style={{...inputStyle}} />
          </>
        )}

        {error && <div style={{color:'#EF4444',fontSize:'12px',marginBottom:'10px'}}>{error}</div>}

        <div style={{display:'flex',gap:'10px',marginTop:'6px'}}>
          <button onClick={onClose} disabled={submitting}
            style={{flex:1,padding:'12px',border:'1.5px solid #E0E0DA',borderRadius:'10px',background:'white',fontSize:'14px',fontWeight:'600',cursor:'pointer',color:'#374151'}}>
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={submitting}
            style={{flex:1,padding:'12px',border:'none',borderRadius:'10px',background:'#1A7A4A',color:'white',fontSize:'14px',fontWeight:'700',cursor:'pointer',opacity:submitting?0.7:1}}>
            {submitting ? 'Sending...' : 'Submit Request'}
          </button>
        </div>
      </div>
    </div>
  );
}

function OrderStatusPage({ orderId }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [orderDeleted, setOrderDeleted] = useState(false);
  const [modal, setModal] = useState(null); // null | 'cancel' | 'reschedule'
  const [actionLoading, setActionLoading] = useState(null); // null | 'pickup' | 'swap'
  const [actionError, setActionError] = useState(null);
  const [authError, setAuthError] = useState(false);

  useEffect(function() {
    fetch(BACKEND + '/api/orders/' + encodeURIComponent(orderId))
      .then(function(r) {
        if (r.status === 410) { setOrderDeleted(true); setLoading(false); return null; }
        return r.json();
      })
      .then(function(data) {
        if (!data) return;
        if (data.error) setNotFound(true);
        else setOrder(data.order);
        setLoading(false);
      })
      .catch(function() { setNotFound(true); setLoading(false); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handleRequestPickup() {
    setActionLoading('pickup');
    setActionError(null);
    setAuthError(false);
    clientFetch(BACKEND + '/api/orders/' + encodeURIComponent(orderId) + '/request-pickup', { method: 'POST' })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.error) { setActionError(data.error); }
        else { setOrder(function(o) { return Object.assign({}, o, { status: 'pickup_requested' }); }); }
        setActionLoading(null);
      })
      .catch(function(err) {
        if (err && err.message === 'NOT_AUTHENTICATED') {
          setAuthError(true);
        } else {
          setActionError('Something went wrong. Please call (956) 226-3903.');
        }
        setActionLoading(null);
      });
  }

  function handleRequestSwap() {
    setActionLoading('swap');
    setActionError(null);
    setAuthError(false);
    clientFetch(BACKEND + '/api/orders/' + encodeURIComponent(orderId) + '/request-swap', { method: 'POST' })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.error) { setActionError(data.error); }
        else { setOrder(function(o) { return Object.assign({}, o, { swap_requested: true }); }); }
        setActionLoading(null);
      })
      .catch(function(err) {
        if (err && err.message === 'NOT_AUTHENTICATED') {
          setAuthError(true);
        } else {
          setActionError('Something went wrong. Please call (956) 226-3903.');
        }
        setActionLoading(null);
      });
  }

  const SIZE_LABELS = { '7yd':'7-Yard (Concrete Only)','10yd':'10-Yard','11yd':'11-Yard','13yd':'13-Yard','14yd':'14-Yard','15yd':'15-Yard','17yd':'17-Yard','20yd':'20-Yard','21yd':'21-Yard','25yd':'25-Yard','30yd':'30-Yard','40yd':'40-Yard' };

  const TIMELINE = [
    { label: 'Order Placed',      minProgress: 0 },
    { label: 'Hauler Assigned',   minProgress: 1 },
    { label: 'Delivered',         minProgress: 2 },
    { label: 'Pickup Requested',  minProgress: 3 },
    { label: 'Picked Up',         minProgress: 4 },
  ];

  const STATUS_PROGRESS = { pending: 0, accepted: 1, delivered: 2, pickup_requested: 3, completed: 4 };
  const STATUS_BADGE    = { pending: 'Pending', accepted: 'Hauler Assigned', delivered: 'Delivered', pickup_requested: 'Pickup Requested', completed: 'Picked Up' };

  const navBar = (
    <div style={{background:'#0D0D0D',padding:'16px 24px'}}>
      <div onClick={function(){window.location.href='/';}} style={{fontFamily:'sans-serif',fontSize:'22px',fontWeight:'800',color:'white',cursor:'pointer'}}>
        Tambo
      </div>
    </div>
  );

  if (loading) return (
    <div style={{minHeight:'100vh',background:'#FAFAF8',fontFamily:'sans-serif'}}>
      {navBar}
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',padding:'80px 20px'}}>
        <div style={{color:'#6B6B6B',fontSize:'14px'}}>Loading order...</div>
      </div>
    </div>
  );

  if (orderDeleted) return (
    <div style={{minHeight:'100vh',background:'#FAFAF8',fontFamily:'sans-serif'}}>
      {navBar}
      <div style={{maxWidth:'480px',margin:'60px auto',padding:'0 24px',textAlign:'center'}}>
        <div style={{fontSize:'36px',marginBottom:'16px'}}>📦</div>
        <div style={{fontSize:'20px',fontWeight:'700',marginBottom:'8px'}}>This order is no longer available</div>
        <div style={{fontSize:'14px',color:'#6B6B6B',marginBottom:'28px'}}>This order has been removed. If you have questions, call (956) 226-3903.</div>
        <a href="/" style={{background:'#1A7A4A',color:'white',padding:'12px 24px',borderRadius:'10px',fontSize:'14px',fontWeight:'700',textDecoration:'none'}}>Back to Home</a>
      </div>
    </div>
  );

  if (notFound || !order) return (
    <div style={{minHeight:'100vh',background:'#FAFAF8',fontFamily:'sans-serif'}}>
      {navBar}
      <div style={{maxWidth:'480px',margin:'60px auto',padding:'0 24px',textAlign:'center'}}>
        <div style={{fontSize:'36px',marginBottom:'16px'}}>🔍</div>
        <div style={{fontSize:'20px',fontWeight:'700',marginBottom:'8px'}}>Order not found</div>
        <div style={{fontSize:'14px',color:'#6B6B6B',marginBottom:'28px'}}>
          We couldn't find order <strong>{orderId}</strong>. Double-check your order number.
        </div>
        <a href="/" style={{background:'#1A7A4A',color:'white',padding:'12px 24px',borderRadius:'10px',fontSize:'14px',fontWeight:'700',textDecoration:'none'}}>Back to Home</a>
      </div>
    </div>
  );

  const statusNorm = (order.status || '').toLowerCase().replace('-', '_');
  const progress   = STATUS_PROGRESS[statusNorm] !== undefined ? STATUS_PROGRESS[statusNorm] : 0;
  const badgeLabel = STATUS_BADGE[statusNorm] || order.status;
  const sizeLabel  = SIZE_LABELS[order.size] || order.size;
  const address    = order.guest_address || order.delivery_address;
  const deliveryDate = (() => {
    if (!order.requested_delivery_date) return null;
    try {
      const d = new Date(String(order.requested_delivery_date).split('T')[0] + 'T12:00:00');
      if (isNaN(d.getTime())) return null;
      return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
    } catch { return null; }
  })();
  const windowStr = formatTimeWindow(order.delivery_window_start, order.delivery_window_end);
  const rowStyle = {display:'flex',justifyContent:'space-between',alignItems:'flex-start',padding:'10px 0',borderBottom:'1px solid #F0F0EC',fontSize:'14px',gap:'12px'};

  return (
    <div style={{minHeight:'100vh',background:'#FAFAF8',fontFamily:'sans-serif'}}>
      {navBar}
      <div style={{background:'#1A7A4A',padding:'20px 24px',color:'white'}}>
        <div style={{fontSize:'11px',fontWeight:'700',opacity:.7,textTransform:'uppercase',letterSpacing:'.8px',marginBottom:'6px'}}>Order Status</div>
        <div style={{fontSize:'26px',fontWeight:'800',marginBottom:'8px'}}>{order.order_number}</div>
        <span style={{background:'rgba(255,255,255,0.25)',borderRadius:'20px',padding:'4px 14px',fontSize:'13px',fontWeight:'600'}}>
          {badgeLabel}
        </span>
      </div>

      <div style={{maxWidth:'480px',margin:'0 auto',padding:'20px'}}>
        {/* Timeline */}
        <div style={{background:'white',borderRadius:'14px',padding:'20px',marginBottom:'14px',border:'1px solid #E8E8E4'}}>
          <div style={{fontSize:'11px',fontWeight:'700',color:'#6B6B6B',textTransform:'uppercase',letterSpacing:'.8px',marginBottom:'18px'}}>Progress</div>
          {TIMELINE.map(function(step, i) {
            const isDone    = progress >= step.minProgress;
            const isCurrent = progress === step.minProgress;
            const isLast    = i === TIMELINE.length - 1;
            return (
              <div key={step.label} style={{display:'flex',alignItems:'flex-start'}}>
                <div style={{display:'flex',flexDirection:'column',alignItems:'center',marginRight:'14px',flexShrink:0}}>
                  <div style={{width:'26px',height:'26px',borderRadius:'50%',background:isDone?'#1A7A4A':'#E8E8E4',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'13px',fontWeight:'800',color:isDone?'white':'#9CA3AF'}}>
                    {isDone ? '✓' : ''}
                  </div>
                  {!isLast && <div style={{width:'2px',height:'28px',background:progress > step.minProgress ? '#1A7A4A' : '#E8E8E4'}}/>}
                </div>
                <div style={{paddingTop:'3px',paddingBottom:isLast?'0':'8px',flex:1}}>
                  <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
                    <span style={{fontSize:'14px',fontWeight:isCurrent?'700':isDone?'600':'400',color:isDone?'#0D0D0D':'#9CA3AF'}}>
                      {step.label}
                    </span>
                    {isCurrent && (
                      <span style={{fontSize:'11px',background:'#E8F5EE',color:'#1A7A4A',borderRadius:'10px',padding:'2px 8px',fontWeight:'700'}}>Now</span>
                    )}
                  </div>
                  {step.label === 'Hauler Assigned' && isDone && order.hauler_first_name && (
                    <div style={{fontSize:'12px',color:'#6B6B6B',marginTop:'3px'}}>Hauler: {order.hauler_first_name}</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Order Details */}
        <div style={{background:'white',borderRadius:'14px',padding:'20px',marginBottom:'14px',border:'1px solid #E8E8E4'}}>
          <div style={{fontSize:'11px',fontWeight:'700',color:'#6B6B6B',textTransform:'uppercase',letterSpacing:'.8px',marginBottom:'14px'}}>Order Details</div>
          <div style={rowStyle}><span style={{color:'#6B6B6B',flexShrink:0}}>Dumpster</span><span style={{fontWeight:'600'}}>{sizeLabel} Dumpster</span></div>
          {address && <div style={rowStyle}><span style={{color:'#6B6B6B',flexShrink:0}}>Delivery Address</span><span style={{fontWeight:'600',textAlign:'right'}}>{address}</span></div>}
          {deliveryDate && <div style={rowStyle}><span style={{color:'#6B6B6B',flexShrink:0}}>Delivery Date</span><span style={{fontWeight:'600'}}>{deliveryDate}</span></div>}
          {windowStr && <div style={rowStyle}><span style={{color:'#6B6B6B',flexShrink:0}}>Time Window</span><span style={{fontWeight:'600'}}>{windowStr}</span></div>}
          {order.hauler_first_name && <div style={{...rowStyle,borderBottom:'none'}}><span style={{color:'#6B6B6B',flexShrink:0}}>Your Hauler</span><span style={{fontWeight:'600'}}>{order.hauler_first_name}</span></div>}
        </div>

        {/* Your Information */}
        {(order.guest_name || address) && (
          <div style={{background:'white',borderRadius:'14px',padding:'20px',marginBottom:'14px',border:'1px solid #E8E8E4'}}>
            <div style={{fontSize:'11px',fontWeight:'700',color:'#6B6B6B',textTransform:'uppercase',letterSpacing:'.8px',marginBottom:'14px'}}>Your Information</div>
            {order.guest_name && <div style={rowStyle}><span style={{color:'#6B6B6B',flexShrink:0}}>Name</span><span style={{fontWeight:'600'}}>{order.guest_name}</span></div>}
            {address && <div style={{...rowStyle,borderBottom:'none'}}><span style={{color:'#6B6B6B',flexShrink:0}}>Service Address</span><span style={{fontWeight:'600',textAlign:'right'}}>{address}</span></div>}
          </div>
        )}

        {/* Request Pickup / Swap — once delivered or accepted */}
        {(statusNorm === 'delivered' || statusNorm === 'accepted') && (
          <div style={{background:'white',borderRadius:'14px',padding:'18px 20px',marginBottom:'14px',border:'1px solid #E8E8E4'}}>
            <div style={{fontSize:'11px',fontWeight:'700',color:'#6B6B6B',textTransform:'uppercase',letterSpacing:'.8px',marginBottom:'14px'}}>Dumpster Actions</div>
            {authError ? (
              <div style={{background:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:'10px',padding:'12px 14px',marginBottom:'10px'}}>
                <div style={{fontSize:'13px',color:'#1E40AF',lineHeight:'1.5',marginBottom:'4px'}}>
                  Log in to request this, or text <strong>READY</strong> (pickup) / <strong>SWAP</strong> (swap) to us.
                </div>
                <div style={{fontSize:'12px',color:'#3B82F6',lineHeight:'1.5',marginBottom:'12px'}}>
                  Inicia sesión para solicitarlo, o envíanos <strong>READY</strong> (recoger) / <strong>SWAP</strong> (cambio) por mensaje.
                </div>
                <a href={'/login?next=' + encodeURIComponent('/order/' + orderId)}
                  style={{display:'inline-block',background:'#1A7A4A',color:'white',borderRadius:'8px',padding:'8px 18px',fontSize:'13px',fontWeight:'700',textDecoration:'none'}}>
                  Log In / Iniciar sesión
                </a>
              </div>
            ) : actionError ? (
              <div style={{fontSize:'12px',fontWeight:'700',background:'#FEF2F2',color:'#DC2626',padding:'8px 12px',borderRadius:'8px',marginBottom:'10px'}}>
                {actionError}
              </div>
            ) : null}
            {order.swap_requested && (
              <div style={{fontSize:'12px',fontWeight:'700',background:'#FEF3C7',color:'#D97706',padding:'8px 12px',borderRadius:'8px',marginBottom:'8px'}}>
                Swap request received — your hauler has been notified.
              </div>
            )}
            <div style={{display:'flex',gap:'10px'}}>
              <button
                onClick={handleRequestPickup}
                disabled={actionLoading !== null}
                style={{flex:1,padding:'10px',border:'1.5px solid #1A7A4A',borderRadius:'10px',background:'#1A7A4A',color:'white',fontSize:'13px',fontWeight:'700',cursor:actionLoading?'not-allowed':'pointer',opacity:actionLoading?0.6:1}}>
                {actionLoading === 'pickup' ? 'Requesting...' : 'Request Pickup'}
              </button>
              {!order.swap_requested && (
                <button
                  onClick={handleRequestSwap}
                  disabled={actionLoading !== null}
                  style={{flex:1,padding:'10px',border:'1.5px solid #6B6B6B',borderRadius:'10px',background:'white',color:'#374151',fontSize:'13px',fontWeight:'700',cursor:actionLoading?'not-allowed':'pointer',opacity:actionLoading?0.6:1}}>
                  {actionLoading === 'swap' ? 'Requesting...' : 'Swap Dumpster'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Cancel / Reschedule — only for active orders */}
        {statusNorm !== 'completed' && statusNorm !== 'cancelled' && statusNorm !== 'delivered' && (
          <div style={{background:'white',borderRadius:'14px',padding:'18px 20px',marginBottom:'14px',border:'1px solid #E8E8E4'}}>
            <div style={{fontSize:'11px',fontWeight:'700',color:'#6B6B6B',textTransform:'uppercase',letterSpacing:'.8px',marginBottom:'14px'}}>Modify Order</div>
            {order.cancel_requested && (
              <div style={{fontSize:'12px',fontWeight:'700',background:'#FEF3C7',color:'#D97706',padding:'8px 12px',borderRadius:'8px',marginBottom:'8px'}}>
                Cancellation request pending — our team will contact you soon.
              </div>
            )}
            {order.reschedule_requested && (
              <div style={{fontSize:'12px',fontWeight:'700',background:'#FEF3C7',color:'#D97706',padding:'8px 12px',borderRadius:'8px',marginBottom:'8px'}}>
                Reschedule request pending — our team will confirm the new date.
              </div>
            )}
            {!order.cancel_requested && !order.reschedule_requested && (
              <div style={{display:'flex',gap:'10px'}}>
                <button onClick={function(){setModal('reschedule');}}
                  style={{flex:1,padding:'10px',border:'1.5px solid #1A7A4A',borderRadius:'10px',background:'white',color:'#1A7A4A',fontSize:'13px',fontWeight:'700',cursor:'pointer'}}>
                  Request Reschedule
                </button>
                <button onClick={function(){setModal('cancel');}}
                  style={{flex:1,padding:'10px',border:'1.5px solid #EF4444',borderRadius:'10px',background:'white',color:'#EF4444',fontSize:'13px',fontWeight:'700',cursor:'pointer'}}>
                  Request Cancel
                </button>
              </div>
            )}
          </div>
        )}

        <div style={{textAlign:'center',padding:'12px 0 32px'}}>
          <div style={{fontSize:'13px',color:'#6B6B6B',marginBottom:'8px'}}>Questions about your order?</div>
          <a href="tel:+19562753688" style={{color:'#1A7A4A',fontWeight:'700',fontSize:'14px',textDecoration:'none'}}>Call (956) 275-3688</a>
        </div>
      </div>

      {modal && (
        <OrderRequestModal
          type={modal}
          order={order}
          onClose={function(){ setModal(null); }}
        />
      )}
    </div>
  );
}

function PrivacyPage() {
  const h2 = {fontSize:'17px',fontWeight:'700',marginBottom:'10px',color:'#0D0D0D',fontFamily:'sans-serif'};
  const body = {fontSize:'14px',color:'#374151',lineHeight:'1.7'};
  const ul = {fontSize:'14px',color:'#374151',lineHeight:'1.85',paddingLeft:'22px'};
  const sub = {fontSize:'14px',fontWeight:'600',color:'#0D0D0D',marginBottom:'5px',marginTop:'10px'};
  const sec = {marginBottom:'32px'};
  return (
    <div style={{minHeight:'100vh',background:'#FAFAF8',fontFamily:'sans-serif'}}>
      <div style={{background:'#0D0D0D',padding:'16px 24px'}}>
        <div onClick={function(){window.location.href='/';}} style={{fontSize:'22px',fontWeight:'800',color:'white',cursor:'pointer'}}>Tambo</div>
      </div>
      <div style={{maxWidth:'720px',margin:'0 auto',padding:'40px 24px 72px'}}>
        <h1 style={{fontSize:'28px',fontWeight:'800',marginBottom:'4px',fontFamily:'sans-serif',color:'#0D0D0D'}}>Privacy Policy</h1>
        <p style={{...body,color:'#6b7280',marginTop:'4px',marginBottom:'2px'}}>Tambo Technologies LLC ("Tambo," "we," "us," or "our")</p>
        <p style={{...body,color:'#6b7280',marginTop:0,marginBottom:'32px'}}>Effective Date: July 3, 2026</p>
        <p style={{...body,marginBottom:'36px'}}>This Privacy Policy explains how Tambo Technologies LLC collects, uses, and protects your information when you use our website fasttambo.com and our dumpster rental marketplace services (the "Service").</p>

        <div style={sec}>
          <h2 style={h2}>1. Information We Collect</h2>
          <p style={{...body,marginBottom:'8px'}}>We collect the following types of information:</p>
          <p style={sub}>Information you provide directly:</p>
          <ul style={{...ul,marginBottom:'12px'}}>
            <li>Name, email address, phone number, and physical/service address</li>
            <li>Business or company name (for contractors and haulers)</li>
            <li>Payment information (processed securely through our payment processor; we do not store full card numbers)</li>
            <li>Details about your dumpster rental order (size, delivery location, dates, waste type)</li>
            <li>For haulers: business details, service area, dumpster inventory, and insurance information</li>
          </ul>
          <p style={sub}>Information collected automatically:</p>
          <ul style={{...ul,marginBottom:'12px'}}>
            <li>Device and browser information</li>
            <li>IP address and general location</li>
            <li>Usage data about how you interact with our website</li>
          </ul>
          <p style={sub}>Location information:</p>
          <p style={{...body,margin:0}}>When you search for dumpster availability, we use the address, city, or ZIP code you provide to match you with nearby haulers.</p>
        </div>

        <div style={sec}>
          <h2 style={h2}>2. How We Use Your Information</h2>
          <p style={{...body,marginBottom:'8px'}}>We use your information to:</p>
          <ul style={{...ul,margin:0}}>
            <li>Provide and operate the Service, including matching clients with haulers</li>
            <li>Process orders, payments, and payouts</li>
            <li>Send you order confirmations, delivery updates, and service-related communications by email and SMS text message</li>
            <li>Coordinate dumpster delivery, pickup, and scheduling</li>
            <li>Provide customer support</li>
            <li>Improve and maintain the Service</li>
            <li>Comply with legal obligations</li>
          </ul>
        </div>

        <div style={sec}>
          <h2 style={h2}>3. SMS Text Messaging</h2>
          <p style={{...body,marginBottom:'10px'}}>By providing your phone number and opting in, you consent to receive SMS text messages from Tambo related to your use of the Service. This may include order confirmations, delivery and pickup coordination, availability check-ins (for haulers), dispatch notifications, and other service-related messages.</p>
          <ul style={{...ul,marginBottom:'12px'}}>
            <li>Message frequency varies based on your activity and orders.</li>
            <li>Message and data rates may apply.</li>
            <li>You can opt out at any time by replying <strong>STOP</strong> to any message. After you reply STOP, you will receive a confirmation and no further messages, unless you opt back in.</li>
            <li>For help, reply <strong>HELP</strong> or contact us at <a href="mailto:office@tambodumpsters.com" style={{color:'#1A7A4A'}}>office@tambodumpsters.com</a> or 956-226-3903.</li>
            <li>Consent to receive text messages is not a condition of purchasing any goods or services.</li>
          </ul>
          <p style={{...body,margin:0}}>We do not share your mobile phone number or SMS consent with third parties or affiliates for their marketing purposes. No mobile information will be shared with third parties for marketing or promotional purposes.</p>
        </div>

        <div style={sec}>
          <h2 style={h2}>4. How We Share Information</h2>
          <p style={{...body,marginBottom:'10px'}}>We share your information only as needed to operate the Service:</p>
          <ul style={{...ul,marginBottom:'12px'}}>
            <li><strong>With haulers and clients:</strong> To fulfill an order, we share necessary details between the client and the assigned hauler (such as delivery address and contact information) so the service can be completed.</li>
            <li><strong>With service providers:</strong> We use trusted third-party providers to operate our Service, including payment processing, SMS/text delivery, email delivery, and cloud hosting. These providers only receive the information necessary to perform their services.</li>
            <li><strong>For legal reasons:</strong> We may disclose information if required by law, regulation, legal process, or to protect the rights, safety, or property of Tambo, our users, or others.</li>
          </ul>
          <p style={{...body,margin:0}}>We do not sell your personal information. We do not share your mobile phone number or text-messaging consent with third parties for their own marketing.</p>
        </div>

        <div style={sec}>
          <h2 style={h2}>5. Data Security</h2>
          <p style={{...body,margin:0}}>We take reasonable measures to protect your information, including secure payment processing and restricted access to personal data. However, no method of transmission or storage is completely secure, and we cannot guarantee absolute security.</p>
        </div>

        <div style={sec}>
          <h2 style={h2}>6. Data Retention</h2>
          <p style={{...body,margin:0}}>We retain your information for as long as your account is active or as needed to provide the Service, comply with legal obligations, resolve disputes, and enforce our agreements.</p>
        </div>

        <div style={sec}>
          <h2 style={h2}>7. Your Choices</h2>
          <ul style={{...ul,margin:0}}>
            <li>You may update your account information by contacting us.</li>
            <li>You may opt out of SMS messages at any time by replying <strong>STOP</strong>.</li>
            <li>You may request deletion of your account and associated data by contacting us at <a href="mailto:office@tambodumpsters.com" style={{color:'#1A7A4A'}}>office@tambodumpsters.com</a>, subject to any records we are required to retain by law.</li>
          </ul>
        </div>

        <div style={sec}>
          <h2 style={h2}>8. Children's Privacy</h2>
          <p style={{...body,margin:0}}>The Service is not directed to individuals under 18, and we do not knowingly collect personal information from children.</p>
        </div>

        <div style={sec}>
          <h2 style={h2}>9. Changes to This Policy</h2>
          <p style={{...body,margin:0}}>We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated effective date. Your continued use of the Service after changes are posted constitutes acceptance of the updated policy.</p>
        </div>

        <div style={sec}>
          <h2 style={h2}>10. Contact Us</h2>
          <p style={{...body,marginBottom:'12px'}}>If you have questions about this Privacy Policy or your information, contact us:</p>
          <div style={{background:'#f3f4f6',borderRadius:'8px',padding:'16px 20px',fontSize:'14px',color:'#374151',lineHeight:'1.8'}}>
            <strong>Tambo Technologies LLC</strong><br/>
            Email: <a href="mailto:office@tambodumpsters.com" style={{color:'#1A7A4A'}}>office@tambodumpsters.com</a><br/>
            Phone: <a href="tel:9562263903" style={{color:'#1A7A4A'}}>956-226-3903</a>
          </div>
          <p style={{...body,marginTop:'16px',marginBottom:0,color:'#6b7280',fontSize:'13px'}}>This Privacy Policy is provided for general informational purposes and does not constitute legal advice.</p>
        </div>
      </div>
      <footer style={{background:'#111',color:'white',padding:'24px',textAlign:'center'}}>
        <div onClick={function(){window.location.href='/';}} style={{fontWeight:'800',fontSize:'20px',marginBottom:'10px',cursor:'pointer'}}>Tambo</div>
        <div style={{marginBottom:'8px'}}>
          <a href="/privacy"    style={{color:'#888',textDecoration:'none',fontSize:'12px',margin:'0 10px'}}>Privacy Policy</a>
          <a href="/terms"      style={{color:'#888',textDecoration:'none',fontSize:'12px',margin:'0 10px'}}>Terms of Service</a>
          <a href="/sms-opt-in" style={{color:'#888',textDecoration:'none',fontSize:'12px',margin:'0 10px'}}>SMS Consent</a>
        </div>
        <div style={{fontSize:'12px',color:'#555'}}>© 2026 Tambo Technologies, LLC. All rights reserved.</div>
      </footer>
    </div>
  );
}

function SmsOptInPage() {
  const h1 = {fontSize:'28px',fontWeight:'800',marginBottom:'8px',fontFamily:'sans-serif',color:'#0D0D0D'};
  const h2 = {fontSize:'18px',fontWeight:'700',marginBottom:'12px',color:'#0D0D0D',fontFamily:'sans-serif',marginTop:0};
  const body = {fontSize:'14px',color:'#374151',lineHeight:'1.75',fontFamily:'sans-serif'};
  const sec = {marginBottom:'40px',paddingBottom:'40px',borderBottom:'1px solid #E5E7EB'};
  return (
    <div style={{minHeight:'100vh',background:'#FAFAF8',fontFamily:'sans-serif'}}>
      {/* Top nav */}
      <div style={{background:'#0D0D0D',padding:'16px 24px'}}>
        <div onClick={function(){window.location.href='/';}} style={{fontSize:'22px',fontWeight:'800',color:'white',cursor:'pointer',display:'inline-block'}}>
          Tambo
        </div>
      </div>

      {/* Content */}
      <div style={{maxWidth:'720px',margin:'0 auto',padding:'48px 24px 80px'}}>
        <h1 style={h1}>SMS Messaging Consent</h1>
        <p style={{...body,color:'#6b7280',marginTop:'4px',marginBottom:'40px'}}>
          Tambo (Tambo Technologies LLC) sends SMS text messages related to dumpster rental orders and hauler job dispatch. This page documents how users opt in to receive them. Opt-in is optional and unchecked by default in all three flows below.
        </p>

        {/* Client Account Signup */}
        <div style={sec}>
          <h2 style={h2}>1. Client Account Signup</h2>
          <img
            src="/images/optin-client-signup.png"
            alt="Client account signup SMS consent checkbox"
            style={{width:'100%',maxWidth:'640px',borderRadius:'10px',border:'1px solid #E5E7EB',display:'block',marginBottom:'14px'}}
          />
          <p style={{...body,margin:0,color:'#6b7280',fontSize:'13px'}}>
            Shown when repeat clients create an account at fasttambo.com/signup. Unchecked by default and optional.
          </p>
        </div>

        {/* Guest Checkout */}
        <div style={sec}>
          <h2 style={h2}>2. Guest Checkout (Booking)</h2>
          <img
            src="/images/optin-guest-checkout.png"
            alt="Guest checkout SMS consent checkbox"
            style={{width:'100%',maxWidth:'640px',borderRadius:'10px',border:'1px solid #E5E7EB',display:'block',marginBottom:'14px'}}
          />
          <p style={{...body,margin:0,color:'#6b7280',fontSize:'13px'}}>
            Shown when a guest customer books a dumpster rental at fasttambo.com. Unchecked by default and optional.
          </p>
        </div>

        {/* Hauler Registration */}
        <div style={sec}>
          <h2 style={h2}>3. Hauler Registration</h2>
          <img
            src="/images/optin-hauler.png"
            alt="Hauler registration SMS consent checkbox"
            style={{width:'100%',maxWidth:'640px',borderRadius:'10px',border:'1px solid #E5E7EB',display:'block',marginBottom:'14px'}}
          />
          <p style={{...body,margin:0,color:'#6b7280',fontSize:'13px'}}>
            Shown when independent haulers register at fasttambo.com/hauler-login. Unchecked by default and optional.
          </p>
        </div>

        {/* Consent language */}
        <div style={{marginBottom:'48px'}}>
          <h2 style={h2}>Consent Language</h2>
          <div style={{background:'#F3F4F6',borderRadius:'10px',padding:'20px 24px',border:'1px solid #E5E7EB'}}>
            <p style={{...body,margin:0,fontSize:'14px',color:'#111827',userSelect:'text',WebkitUserSelect:'text'}}>
              I agree to receive SMS text messages from Tambo about my order (confirmations, delivery/pickup updates) and account. Message and data rates may apply. Message frequency varies. Reply STOP to opt out, HELP for help.
            </p>
          </div>
        </div>

        {/* Legal links */}
        <div style={{display:'flex',gap:'24px',flexWrap:'wrap'}}>
          <a href="/privacy" style={{color:'#1A7A4A',textDecoration:'none',fontSize:'14px',fontWeight:'600'}}>Privacy Policy</a>
          <a href="/terms"   style={{color:'#1A7A4A',textDecoration:'none',fontSize:'14px',fontWeight:'600'}}>Terms of Service</a>
        </div>
      </div>

      {/* Footer */}
      <footer style={{background:'#111',color:'white',padding:'24px',textAlign:'center'}}>
        <div onClick={function(){window.location.href='/';}} style={{fontWeight:'800',fontSize:'20px',marginBottom:'10px',cursor:'pointer'}}>Tambo</div>
        <div style={{marginBottom:'8px'}}>
          <a href="/privacy"     style={{color:'#888',textDecoration:'none',fontSize:'12px',margin:'0 10px'}}>Privacy Policy</a>
          <a href="/terms"       style={{color:'#888',textDecoration:'none',fontSize:'12px',margin:'0 10px'}}>Terms of Service</a>
          <a href="/sms-opt-in"  style={{color:'#888',textDecoration:'none',fontSize:'12px',margin:'0 10px'}}>SMS Consent</a>
        </div>
        <div style={{fontSize:'12px',color:'#555'}}>© 2026 Tambo Technologies, LLC. All rights reserved.</div>
      </footer>
    </div>
  );
}

function TermsPage() {
  const h2 = {fontSize:'17px',fontWeight:'700',marginBottom:'10px',color:'#0D0D0D',fontFamily:'sans-serif'};
  const body = {fontSize:'14px',color:'#374151',lineHeight:'1.7'};
  const ul = {fontSize:'14px',color:'#374151',lineHeight:'1.85',paddingLeft:'22px'};
  const sec = {marginBottom:'32px'};
  return (
    <div style={{minHeight:'100vh',background:'#FAFAF8',fontFamily:'sans-serif'}}>
      <div style={{background:'#0D0D0D',padding:'16px 24px'}}>
        <div onClick={function(){window.location.href='/';}} style={{fontSize:'22px',fontWeight:'800',color:'white',cursor:'pointer'}}>Tambo</div>
      </div>
      <div style={{maxWidth:'720px',margin:'0 auto',padding:'40px 24px 72px'}}>
        <h1 style={{fontSize:'28px',fontWeight:'800',marginBottom:'4px',fontFamily:'sans-serif',color:'#0D0D0D'}}>Terms of Service</h1>
        <p style={{...body,color:'#6b7280',marginTop:'4px',marginBottom:'2px'}}>Tambo Technologies LLC ("Tambo," "we," "us," or "our")</p>
        <p style={{...body,color:'#6b7280',marginTop:0,marginBottom:'32px'}}>Effective Date: July 3, 2026</p>
        <p style={{...body,marginBottom:'36px'}}>Welcome to Tambo. These Terms of Service ("Terms") govern your use of fasttambo.com and our dumpster rental marketplace services (the "Service"). By using the Service, you agree to these Terms. If you do not agree, do not use the Service.</p>

        <div style={sec}>
          <h2 style={h2}>1. What Tambo Does</h2>
          <p style={{...body,marginBottom:'10px'}}>Tambo operates a marketplace that connects customers who need roll-off dumpster rentals ("Clients") with independent dumpster haulers ("Haulers"). Tambo facilitates ordering, scheduling, payment, and coordination between Clients and Haulers.</p>
          <p style={{...body,margin:0}}>Depending on the transaction, Tambo may act as a broker arranging service on your behalf or as a marketplace platform. The actual delivery, placement, and pickup of dumpsters is performed by independent Haulers, not by Tambo.</p>
        </div>

        <div style={sec}>
          <h2 style={h2}>2. Eligibility</h2>
          <p style={{...body,margin:0}}>You must be at least 18 years old and able to enter into a binding contract to use the Service.</p>
        </div>

        <div style={sec}>
          <h2 style={h2}>3. Accounts</h2>
          <p style={{...body,margin:0}}>To use certain features, you must create an account and provide accurate, complete information. You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account. Notify us immediately of any unauthorized use.</p>
        </div>

        <div style={sec}>
          <h2 style={h2}>4. Orders, Pricing, and Payment</h2>
          <ul style={{...ul,margin:0}}>
            <li>Prices shown to Clients include Tambo's service fee.</li>
            <li>When you place an order, you authorize us to charge your payment method for the total amount shown at checkout.</li>
            <li>Payment is processed through our third-party payment processor. By providing payment information, you agree to that processor's terms.</li>
            <li>Additional charges may apply for services beyond the original order (for example, overage tonnage, extended rental periods, or additional trips), as disclosed to you.</li>
            <li>Orders are subject to Hauler availability. If no Hauler can fulfill an order, we will notify you and issue any applicable refund.</li>
          </ul>
        </div>

        <div style={sec}>
          <h2 style={h2}>5. Cancellations and Refunds</h2>
          <p style={{...body,margin:0}}>Cancellation and refund terms may depend on the timing of your request and the status of your order (for example, whether a dumpster has already been dispatched or delivered). Contact us as early as possible to request a change or cancellation. We will communicate any applicable fees or refunds based on the circumstances of your order.</p>
        </div>

        <div style={sec}>
          <h2 style={h2}>6. SMS Text Messaging</h2>
          <p style={{...body,marginBottom:'10px'}}>By providing your phone number and opting in, you agree to receive SMS text messages from Tambo related to the Service, which may include order confirmations, delivery and pickup coordination, dispatch notifications, availability check-ins (for Haulers), and other service-related messages.</p>
          <ul style={{...ul,margin:0}}>
            <li>Message frequency varies based on your activity and orders.</li>
            <li>Message and data rates may apply.</li>
            <li>You can opt out at any time by replying <strong>STOP</strong>. You will receive a confirmation message, after which you will not receive further texts unless you opt back in.</li>
            <li>For help, reply <strong>HELP</strong> or contact <a href="mailto:office@tambodumpsters.com" style={{color:'#1A7A4A'}}>office@tambodumpsters.com</a> or 956-226-3903.</li>
            <li>Consent to receive text messages is not a condition of purchasing any goods or services.</li>
            <li>Supported carriers are not liable for delayed or undelivered messages.</li>
          </ul>
        </div>

        <div style={sec}>
          <h2 style={h2}>7. Responsibilities of Clients</h2>
          <p style={{...body,marginBottom:'10px'}}>You agree to:</p>
          <ul style={{...ul,marginBottom:'12px'}}>
            <li>Provide accurate delivery location and access information.</li>
            <li>Ensure a safe, legal, and accessible placement location for the dumpster.</li>
            <li>Not overload dumpsters beyond weight limits or place prohibited materials (such as hazardous waste, and, for certain sizes, materials not permitted for that size) in them.</li>
            <li>Follow the Hauler's guidelines regarding acceptable materials, weight limits, and rental duration.</li>
            <li>Be responsible for any damage, fines, or additional charges resulting from improper use, prohibited materials, or overloading.</li>
          </ul>
          <p style={{...body,margin:0}}>Certain dumpster sizes may be restricted to specific materials (for example, sizes designated for concrete or heavy debris only). You agree to use such dumpsters only for their designated purpose.</p>
        </div>

        <div style={sec}>
          <h2 style={h2}>8. Responsibilities of Haulers</h2>
          <p style={{...body,marginBottom:'10px'}}>If you use the Service as a Hauler, you agree to:</p>
          <ul style={{...ul,margin:0}}>
            <li>Provide accurate business, service area, insurance, and availability information.</li>
            <li>Maintain any licenses, permits, and insurance required to legally provide dumpster hauling services.</li>
            <li>Fulfill accepted orders in a timely, professional manner.</li>
            <li>Communicate availability and order status accurately, including through SMS check-ins and dispatch responses.</li>
            <li>Operate as an independent contractor. Haulers are not employees, agents, or partners of Tambo.</li>
          </ul>
        </div>

        <div style={sec}>
          <h2 style={h2}>9. Independent Contractors</h2>
          <p style={{...body,margin:0}}>Haulers are independent businesses. Tambo does not control and is not responsible for the manner in which Haulers perform their services. Tambo does not guarantee the quality, safety, legality, or timeliness of any Hauler's services beyond the coordination the platform provides.</p>
        </div>

        <div style={sec}>
          <h2 style={h2}>10. Disclaimers</h2>
          <div style={{background:'#f3f4f6',borderRadius:'8px',padding:'14px 18px',fontSize:'13px',color:'#374151',lineHeight:'1.7',fontWeight:'500'}}>
            THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. Tambo does not warrant that the Service will be uninterrupted, error-free, or secure.
          </div>
        </div>

        <div style={sec}>
          <h2 style={h2}>11. Limitation of Liability</h2>
          <div style={{background:'#f3f4f6',borderRadius:'8px',padding:'14px 18px',fontSize:'13px',color:'#374151',lineHeight:'1.7',fontWeight:'500'}}>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, TAMBO TECHNOLOGIES LLC WILL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, ARISING FROM YOUR USE OF THE SERVICE. TAMBO'S TOTAL LIABILITY FOR ANY CLAIM RELATED TO THE SERVICE WILL NOT EXCEED THE AMOUNT YOU PAID TO TAMBO FOR THE ORDER GIVING RISE TO THE CLAIM.
          </div>
        </div>

        <div style={sec}>
          <h2 style={h2}>12. Indemnification</h2>
          <p style={{...body,margin:0}}>You agree to indemnify and hold harmless Tambo Technologies LLC and its owners, officers, and employees from any claims, damages, losses, or expenses (including reasonable attorneys' fees) arising from your use of the Service, your violation of these Terms, or your violation of any law or the rights of a third party.</p>
        </div>

        <div style={sec}>
          <h2 style={h2}>13. Dispute Resolution and Governing Law</h2>
          <p style={{...body,margin:0}}>These Terms are governed by the laws of the State of Texas, without regard to its conflict-of-laws rules. Any disputes arising from these Terms or the Service will be resolved in the state or federal courts located in Texas, and you consent to the jurisdiction of those courts.</p>
        </div>

        <div style={sec}>
          <h2 style={h2}>14. Changes to These Terms</h2>
          <p style={{...body,margin:0}}>We may update these Terms from time to time. Changes will be posted on this page with an updated effective date. Your continued use of the Service after changes are posted constitutes acceptance of the updated Terms.</p>
        </div>

        <div style={sec}>
          <h2 style={h2}>15. Contact Us</h2>
          <div style={{background:'#f3f4f6',borderRadius:'8px',padding:'16px 20px',fontSize:'14px',color:'#374151',lineHeight:'1.8',marginBottom:'16px'}}>
            <strong>Tambo Technologies LLC</strong><br/>
            Email: <a href="mailto:office@tambodumpsters.com" style={{color:'#1A7A4A'}}>office@tambodumpsters.com</a><br/>
            Phone: <a href="tel:9562263903" style={{color:'#1A7A4A'}}>956-226-3903</a>
          </div>
          <p style={{...body,marginBottom:0,color:'#6b7280',fontSize:'13px'}}>These Terms of Service are provided for general informational purposes and do not constitute legal advice. Tambo recommends review by a licensed attorney before relying on these Terms for your business.</p>
        </div>
      </div>
      <footer style={{background:'#111',color:'white',padding:'24px',textAlign:'center'}}>
        <div onClick={function(){window.location.href='/';}} style={{fontWeight:'800',fontSize:'20px',marginBottom:'10px',cursor:'pointer'}}>Tambo</div>
        <div style={{marginBottom:'8px'}}>
          <a href="/privacy"    style={{color:'#888',textDecoration:'none',fontSize:'12px',margin:'0 10px'}}>Privacy Policy</a>
          <a href="/terms"      style={{color:'#888',textDecoration:'none',fontSize:'12px',margin:'0 10px'}}>Terms of Service</a>
          <a href="/sms-opt-in" style={{color:'#888',textDecoration:'none',fontSize:'12px',margin:'0 10px'}}>SMS Consent</a>
        </div>
        <div style={{fontSize:'12px',color:'#555'}}>© 2026 Tambo Technologies, LLC. All rights reserved.</div>
      </footer>
    </div>
  );
}

function PayNowButton({ invoiceId, balance, staticLink }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  function handlePay() {
    if (staticLink) { window.location.href = staticLink; return; }
    setLoading(true); setErr('');
    fetch(BACKEND + '/api/invoices/' + encodeURIComponent(invoiceId) + '/pay-link', { method: 'POST' })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.url) { window.location.href = d.url; }
        else { setErr(d.error || 'Could not create payment link.'); setLoading(false); }
      })
      .catch(function() { setErr('Network error. Please try again.'); setLoading(false); });
  }

  return (
    <div style={{textAlign:'center',marginTop:'24px',paddingTop:'20px',borderTop:'1px solid #F0F0EC'}}>
      <button onClick={handlePay} disabled={loading}
        style={{display:'inline-block',padding:'15px 48px',background:loading?'#555':'#0D0D0D',color:'white',border:'none',borderRadius:'10px',fontWeight:'700',fontSize:'16px',cursor:loading?'not-allowed':'pointer'}}>
        {loading ? 'Redirecting…' : 'Pay Now · $' + Number(balance).toFixed(2)}
      </button>
      {err && <div style={{color:'#ef4444',fontSize:'13px',marginTop:'8px'}}>{err}</div>}
      <div style={{fontSize:'12px',color:'#9ca3af',marginTop:'8px'}}>Secure payment powered by Stripe</div>
    </div>
  );
}

function InvoicePage({ invoiceId }) {
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(function() {
    fetch(BACKEND + '/api/invoices/' + encodeURIComponent(invoiceId))
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data.error || !data.invoice) setNotFound(true);
        else setInvoice(data.invoice);
        setLoading(false);
      })
      .catch(function() { setNotFound(true); setLoading(false); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(function() {
    if (!invoice) return;
    supabase.auth.getSession().then(function(result) {
      var session = result.data && result.data.session;
      if (session && session.user && session.user.email === 'jesustrevi1020@gmail.com') return;
      var src = new URLSearchParams(window.location.search).get('src') || 'direct';
      fetch(BACKEND + '/api/invoices/' + encodeURIComponent(invoiceId) + '/viewed', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ src: src }),
      }).catch(function() {});
    });
  }, [invoice]); // eslint-disable-line react-hooks/exhaustive-deps

  function fmtD(raw) {
    if (!raw) return '—';
    return new Date(String(raw).split('T')[0] + 'T12:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }
  function fm(n) { return '$' + Number(n || 0).toFixed(2); }
  function invNum(inv) { return inv.invoice_ref || inv.invoice_display || ('INV-' + ((inv.invoice_number || 0) + 1000)); }
  function invSt(inv) {
    if (inv.status === 'void') return 'void';
    if (inv.status === 'paid') return 'paid';
    if ((inv.status === 'partially_paid' || inv.status === 'sent') && inv.due_date) {
      const nowCT = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
      const dueStr = String(inv.due_date).split('T')[0];
      if (nowCT > dueStr) return 'overdue';
    }
    return inv.status || 'draft';
  }

  const navBar = (
    <div style={{background:'#0D0D0D',padding:'16px 24px'}}>
      <div onClick={function(){window.location.href='/';}} style={{fontFamily:'sans-serif',fontSize:'22px',fontWeight:'800',color:'white',cursor:'pointer'}}>
        Tambo
      </div>
    </div>
  );

  if (loading) return (
    <div style={{minHeight:'100vh',background:'#FAFAF8',fontFamily:'sans-serif'}}>
      {navBar}
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',padding:'80px 20px'}}>
        <div style={{color:'#6B6B6B',fontSize:'14px'}}>Loading invoice...</div>
      </div>
    </div>
  );

  if (notFound || !invoice) return (
    <div style={{minHeight:'100vh',background:'#FAFAF8',fontFamily:'sans-serif'}}>
      {navBar}
      <div style={{maxWidth:'480px',margin:'60px auto',padding:'0 24px',textAlign:'center'}}>
        <div style={{fontSize:'36px',marginBottom:'16px'}}>📄</div>
        <div style={{fontSize:'20px',fontWeight:'700',marginBottom:'8px'}}>Invoice not found</div>
        <div style={{fontSize:'14px',color:'#6B6B6B',marginBottom:'28px'}}>We couldn't find this invoice. The link may be invalid or expired.</div>
        <a href="/" style={{background:'#1A7A4A',color:'white',padding:'12px 24px',borderRadius:'10px',fontSize:'14px',fontWeight:'700',textDecoration:'none'}}>Back to Home</a>
      </div>
    </div>
  );

  const ds = invSt(invoice);
  const isPaid = ds === 'paid';
  const isVoid = ds === 'void';
  const isOverdue = ds === 'overdue';
  const isPartial = invoice.status === 'partially_paid';
  const payEntries = (invoice.payment_log_entries || []).filter(function(p) { return p.amount != null; });
  const paymentsTotal = payEntries.reduce(function(s, p) { return s + parseFloat(p.amount || 0); }, 0);
  const balanceDue = isPaid ? (paymentsTotal === 0 ? 0 : Math.max(0, parseFloat(invoice.total || 0) - paymentsTotal))
                            : Math.max(0, Math.round((parseFloat(invoice.total || 0) - paymentsTotal) * 100) / 100);
  const items = invoice.line_items || [];
  const num = invNum(invoice);
  const ST_COLOR = {paid:'#10b981',sent:'#3b82f6',overdue:'#ef4444',draft:'#6b7280',void:'#9ca3af',partially_paid:'#f59e0b'};
  const ST_LABEL = {paid:'Paid',sent:'Sent',overdue:'Overdue',draft:'Draft',void:'Void',partially_paid:'Partial'};

  return (
    <div style={{minHeight:'100vh',background:'#EDEDEA',fontFamily:'sans-serif'}}>
      {navBar}
      <div style={{maxWidth:'680px',margin:'24px auto',padding:'0 16px 48px'}}>

        {/* Status row */}
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'16px',flexWrap:'wrap',gap:'8px'}}>
          <div style={{display:'flex',alignItems:'center',gap:'10px'}}>
            <span style={{fontWeight:'700',fontSize:'16px',color:'#0D0D0D'}}>{num}</span>
            <span style={{background:ST_COLOR[ds]||'#6b7280',color:'white',padding:'3px 10px',borderRadius:'20px',fontSize:'12px',fontWeight:'700'}}>
              {ST_LABEL[ds]||ds}
            </span>
          </div>
          {isOverdue && <div style={{fontSize:'13px',color:'#ef4444',fontWeight:'600'}}>Payment overdue</div>}
          {isVoid && <div style={{fontSize:'13px',color:'#9ca3af',fontWeight:'600'}}>This invoice has been voided</div>}
        </div>

        {/* Invoice card */}
        <div style={{background:'white',borderRadius:'14px',overflow:'hidden',boxShadow:'0 2px 12px rgba(0,0,0,.08)'}}>

          {/* B&W header */}
          <div style={{background:'#0D0D0D',padding:'22px 28px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div>
              <div style={{fontSize:'24px',fontWeight:'900',color:'white',letterSpacing:'1px'}}>Tambo</div>
              <div style={{fontSize:'9px',color:'#888',marginTop:'2px',letterSpacing:'1.2px',textTransform:'uppercase'}}>Fast Local Dumpsters.</div>
            </div>
            <div style={{textAlign:'right'}}>
              <div style={{fontSize:'16px',fontWeight:'800',color:'white'}}>INVOICE</div>
              <div style={{fontSize:'13px',color:'#888',marginTop:'2px'}}>{num}</div>
            </div>
          </div>

          {/* From / Bill To / Details */}
          <div style={{display:'flex',gap:'20px',padding:'20px 28px 16px',flexWrap:'wrap',borderBottom:'1px solid #F0F0EC'}}>
            <div style={{flex:1,minWidth:'130px'}}>
              <div style={{fontSize:'10px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'0.08em',color:'#9ca3af',marginBottom:'6px'}}>From</div>
              <div style={{fontWeight:'700',fontSize:'13px'}}>Tambo Technologies, LLC</div>
              <div style={{fontSize:'12px',color:'#6B6B6B',lineHeight:'1.7',marginTop:'2px'}}>
                1212 East Harrison Avenue<br/>Harlingen, Texas 78550<br/>(844) 878-2626<br/>office@tambodumpsters.com
              </div>
            </div>
            <div style={{flex:1,minWidth:'130px'}}>
              <div style={{fontSize:'10px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'0.08em',color:'#9ca3af',marginBottom:'6px'}}>Bill To</div>
              {invoice.guest_name && <div style={{fontWeight:'700',fontSize:'13px'}}>{invoice.guest_name}</div>}
              {invoice.company_name && <div style={{fontSize:'12px',color:'#374151'}}>{invoice.company_name}</div>}
              {invoice.billing_address && <div style={{fontSize:'12px',color:'#6B6B6B',lineHeight:'1.7',marginTop:'2px'}}>{invoice.billing_address}</div>}
              {invoice.guest_email && <div style={{fontSize:'12px',color:'#6B6B6B',marginTop:'2px'}}>{invoice.guest_email}</div>}
              {invoice.guest_phone && <div style={{fontSize:'12px',color:'#6B6B6B',marginTop:'2px'}}>{invoice.guest_phone}</div>}
              {!invoice.guest_name && !invoice.company_name && <div style={{fontSize:'12px',color:'#9ca3af'}}>—</div>}
            </div>
            {invoice.service_address && (
              <div style={{flex:1,minWidth:'130px'}}>
                <div style={{fontSize:'10px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'0.08em',color:'#9ca3af',marginBottom:'6px'}}>Service Address</div>
                <div style={{fontSize:'12px',color:'#6B6B6B',lineHeight:'1.7'}}>{invoice.service_address}</div>
              </div>
            )}
            <div style={{minWidth:'130px'}}>
              <div style={{fontSize:'10px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'0.08em',color:'#9ca3af',marginBottom:'6px'}}>Details</div>
              <table style={{fontSize:'12px'}}>
                <tbody>
                  <tr><td style={{color:'#9ca3af',paddingRight:'10px',paddingBottom:'3px',whiteSpace:'nowrap'}}>Invoice No</td><td style={{fontWeight:'700'}}>{num}</td></tr>
                  <tr><td style={{color:'#9ca3af',paddingRight:'10px',paddingBottom:'3px'}}>Date</td><td>{fmtD(invoice.created_at)}</td></tr>
                  <tr><td style={{color:'#9ca3af',paddingRight:'10px'}}>Due</td><td style={{color:isOverdue?'#ef4444':undefined,fontWeight:isOverdue?'700':undefined}}>{fmtD(invoice.due_date)}</td></tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Line items */}
          <div style={{padding:'16px 28px'}}>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px',minWidth:'380px'}}>
                <thead>
                  <tr style={{background:'#F7F7F5'}}>
                    {[['Description','left'],['Qty','center'],['Rate','right'],['Amount','right']].map(function([h,a]) {
                      return <th key={h} style={{padding:'8px 10px',textAlign:a,fontSize:'11px',textTransform:'uppercase',letterSpacing:'0.05em',color:'#6B6B6B',fontWeight:'700'}}>{h}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0
                    ? <tr><td colSpan={4} style={{padding:'10px',color:'#9ca3af',textAlign:'center'}}>No items</td></tr>
                    : items.map(function(item, i) {
                        return (
                          <tr key={i}>
                            <td style={{padding:'10px',borderBottom:'1px solid #F0F0EC'}}>{item.description}</td>
                            <td style={{padding:'10px',borderBottom:'1px solid #F0F0EC',textAlign:'center'}}>{item.quantity}</td>
                            <td style={{padding:'10px',borderBottom:'1px solid #F0F0EC',textAlign:'right'}}>{fm(item.rate)}</td>
                            <td style={{padding:'10px',borderBottom:'1px solid #F0F0EC',textAlign:'right',fontWeight:'600'}}>{fm(item.amount)}</td>
                          </tr>
                        );
                      })
                  }
                </tbody>
              </table>
            </div>

            {/* Totals */}
            <table style={{marginLeft:'auto',fontSize:'13px',marginTop:'8px',minWidth:'230px'}}>
              <tbody>
                <tr><td style={{padding:'5px 10px',color:'#6B6B6B'}}>Subtotal</td><td style={{padding:'5px 10px',textAlign:'right'}}>{fm(invoice.subtotal)}</td></tr>
                {invoice.tax_enabled !== false && <tr><td style={{padding:'5px 10px',color:'#6B6B6B'}}>Sales Tax (8.25%)</td><td style={{padding:'5px 10px',textAlign:'right'}}>{fm(invoice.tax_amount)}</td></tr>}
                <tr>
                  <td style={{padding:'8px 10px',fontWeight:'700',borderTop:'2px solid #E8E8E4'}}>Total</td>
                  <td style={{padding:'8px 10px',fontWeight:'700',textAlign:'right',borderTop:'2px solid #E8E8E4'}}>{fm(invoice.total)}</td>
                </tr>
              </tbody>
            </table>

            {/* Payments & Refunds */}
            {isPaid && invoice.paid_at && (
              <div style={{marginTop:'20px',paddingTop:'16px',borderTop:'1px solid #E8E8E4'}}>
                <div style={{fontSize:'11px',fontWeight:'700',textTransform:'uppercase',letterSpacing:'0.06em',color:'#6B6B6B',marginBottom:'8px'}}>Payments &amp; Refunds</div>
                <div style={{display:'flex',justifyContent:'space-between',fontSize:'13px'}}>
                  <span>Payment received {fmtD(invoice.paid_at)}</span>
                  <span style={{color:'#0D0D0D',fontWeight:'600'}}>−{fm(invoice.total)}</span>
                </div>
              </div>
            )}

            {/* Balance Due */}
            <div style={{
              background: isPaid ? '#F0FAF4' : isOverdue ? '#FEF2F2' : '#F7F7F5',
              borderRadius:'8px', padding:'14px 16px', marginTop:'16px',
              display:'flex', justifyContent:'space-between', alignItems:'center',
            }}>
              <span style={{fontWeight:'700',color:isPaid?'#1A7A4A':isOverdue?'#ef4444':'#0D0D0D',fontSize:'14px'}}>
                {isPaid ? 'Paid in Full' : 'Balance Due'}
              </span>
              <span style={{fontWeight:'900',color:isPaid?'#1A7A4A':isOverdue?'#ef4444':'#0D0D0D',fontSize:'18px'}}>
                {fm(balanceDue)}
              </span>
            </div>

            {invoice.notes && (
              <p style={{marginTop:'14px',padding:'10px 14px',background:'#F7F7F5',borderRadius:'8px',fontSize:'12px',color:'#6B6B6B',lineHeight:'1.6'}}>
                <strong>Notes:</strong> {invoice.notes}
              </p>
            )}

            {/* Pay Now */}
            {!isPaid && !isVoid && balanceDue > 0 && (invoice.stripe_payment_link || isPartial) && (
              <PayNowButton invoiceId={invoiceId} balance={balanceDue} staticLink={invoice.stripe_payment_link} />
            )}
            {/* Download PDF */}
            <div style={{textAlign:'center',marginTop:'16px'}}>
              <a href={BACKEND + '/api/invoices/' + encodeURIComponent(invoiceId) + '/pdf'} target="_blank" rel="noopener noreferrer"
                style={{fontSize:'13px',color:'#6b7280',textDecoration:'none',display:'inline-flex',alignItems:'center',gap:'4px'}}>
                ↓ Download PDF
              </a>
            </div>
          </div>

          {/* Card footer */}
          <div style={{background:'#F7F7F5',padding:'14px 28px',textAlign:'center',fontSize:'11px',color:'#9ca3af',borderTop:'1px solid #F0F0EC'}}>
            Tambo Technologies, LLC &middot; 1212 East Harrison Avenue, Harlingen, TX 78550 &middot; (844) 878-2626 &middot; office@tambodumpsters.com
          </div>
        </div>

        <div style={{textAlign:'center',marginTop:'16px',fontSize:'12px',color:'#9ca3af'}}>
          Questions? Call <a href="tel:+18448782626" style={{color:'#1A7A4A'}}>(844) 878-2626</a>
        </div>
      </div>
    </div>
  );
}

function ClientAccountPage() {
  const [acctUser, setAcctUser] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [invLoading, setInvLoading] = useState(false);
  const [accountStatus, setAccountStatus] = useState(null); // null | 'active' | 'pending' | 'rejected'

  useEffect(function() {
    supabase.auth.getSession().then(function({ data }) {
      if (!data.session) { window.location.href = '/login'; return; }
      setAcctUser(data.session.user);
      setPageLoading(false);
      if (data.session.user.id) loadInvoices(data.session.user.id);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadInvoices(userId) {
    setInvLoading(true);
    try {
      const res = await clientFetch(BACKEND + '/api/clients/me');
      if (res.status === 403) {
        const data = await res.json().catch(function() { return {}; });
        setAccountStatus(data.error === 'rejected' ? 'rejected' : 'pending');
        setInvLoading(false);
        return;
      }
      const data = await res.json();
      setInvoices(data.invoices || []);
      setAccountStatus('active');
    } catch (_) { /* silent */ }
    setInvLoading(false);
  }

  function fmtD(raw) {
    if (!raw) return '—';
    return new Date(String(raw).split('T')[0] + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  function fm(n) { return '$' + Number(n || 0).toFixed(2); }
  function dispNum(inv) { return inv.invoice_ref || inv.invoice_display || ('INV-' + ((inv.invoice_number || 0) + 1000)); }
  function invSt(inv) {
    if (inv.status === 'paid') return 'paid';
    if (inv.status === 'sent' && inv.due_date) {
      const nowCT = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
      const dueStr = String(inv.due_date).split('T')[0];
      if (nowCT > dueStr) return 'overdue';
    }
    return inv.status || 'draft';
  }
  const ST_COLOR = { paid: '#10b981', sent: '#3b82f6', overdue: '#ef4444', draft: '#6b7280' };
  const ST_LABEL = { paid: 'Paid', sent: 'Sent', overdue: 'Overdue', draft: 'Draft' };

  const navBar = (
    <div style={{background:'#0D0D0D',padding:'16px 24px',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
      <div onClick={function(){window.location.href='/';}} style={{fontFamily:'sans-serif',fontSize:'22px',fontWeight:'800',color:'white',cursor:'pointer'}}>
        Tambo
      </div>
      {acctUser && (
        <button
          onClick={async function() { await supabase.auth.signOut(); window.location.href = '/'; }}
          style={{background:'none',border:'1px solid #444',color:'#aaa',padding:'6px 14px',borderRadius:'6px',cursor:'pointer',fontSize:'13px'}}
        >
          Sign out
        </button>
      )}
    </div>
  );

  if (pageLoading) return (
    <div style={{minHeight:'100vh',background:'#FAFAF8',fontFamily:'sans-serif'}}>
      {navBar}
      <div style={{display:'flex',alignItems:'center',justifyContent:'center',padding:'80px 20px'}}>
        <div style={{color:'#6B6B6B',fontSize:'14px'}}>Loading...</div>
      </div>
    </div>
  );

  return (
    <div style={{minHeight:'100vh',background:'#FAFAF8',fontFamily:'sans-serif'}}>
      {navBar}
      <div style={{maxWidth:'720px',margin:'0 auto',padding:'28px 20px 60px'}}>

        {/* Welcome */}
        <div style={{marginBottom:'28px'}}>
          <div style={{fontSize:'11px',fontWeight:'700',color:'#9ca3af',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:'4px'}}>Client Account</div>
          <div style={{fontSize:'22px',fontWeight:'800',color:'#0D0D0D',marginBottom:'6px'}}>{acctUser?.email}</div>
          <a href="/" style={{fontSize:'13px',color:'#1A7A4A',textDecoration:'none',fontWeight:'600'}}>+ Order a dumpster</a>
        </div>

        {/* Account status banners */}
        {accountStatus === 'pending' && (
          <div style={{background:'#fffbeb',border:'1px solid #fcd34d',borderRadius:'12px',padding:'20px 24px',marginBottom:'20px'}}>
            <div style={{fontSize:'15px',fontWeight:'700',color:'#92400e',marginBottom:'6px'}}>Account under review</div>
            <div style={{fontSize:'13px',color:'#78350f',lineHeight:'1.6'}}>
              Your Tambo account is being reviewed. We'll send you an email and text when it's approved — usually within one business day.
            </div>
            <div style={{fontSize:'13px',color:'#78350f',marginTop:'8px',fontStyle:'italic'}}>
              Tu cuenta de Tambo está siendo revisada. Te notificaremos por correo y mensaje de texto cuando sea aprobada.
            </div>
          </div>
        )}
        {accountStatus === 'rejected' && (
          <div style={{background:'#fef2f2',border:'1px solid #fca5a5',borderRadius:'12px',padding:'20px 24px',marginBottom:'20px'}}>
            <div style={{fontSize:'15px',fontWeight:'700',color:'#991b1b',marginBottom:'6px'}}>Account not approved</div>
            <div style={{fontSize:'13px',color:'#7f1d1d',lineHeight:'1.6'}}>
              We were unable to approve your account at this time. Please check your email for details or contact us for assistance.
            </div>
          </div>
        )}

        {/* Invoices card — hidden while account is pending or rejected */}
        {(accountStatus === 'active' || accountStatus === null) && (
        <div style={{background:'white',borderRadius:'14px',border:'1px solid #E8E8E4',overflow:'hidden'}}>
          <div style={{padding:'16px 20px',borderBottom:'1px solid #F0F0EC',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div style={{fontSize:'15px',fontWeight:'700',color:'#0D0D0D'}}>Invoices</div>
            {invLoading && <div style={{fontSize:'12px',color:'#9ca3af'}}>Loading...</div>}
          </div>

          {!invLoading && invoices.length === 0 ? (
            <div style={{padding:'40px 20px',textAlign:'center'}}>
              <div style={{fontSize:'28px',marginBottom:'10px'}}>📄</div>
              <div style={{fontSize:'14px',color:'#6B6B6B',marginBottom:'4px'}}>No invoices yet</div>
              <div style={{fontSize:'12px',color:'#9ca3af'}}>Your invoices will appear here after each order.</div>
            </div>
          ) : (
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'13px'}}>
                <thead>
                  <tr>
                    {['Invoice','Date','Due Date','Amount','Status',''].map(function(h) {
                      return <th key={h} style={{padding:'10px 16px',background:'#F9F9F7',textAlign:'left',fontSize:'11px',fontWeight:'700',color:'#6B6B6B',textTransform:'uppercase',letterSpacing:'0.05em',whiteSpace:'nowrap'}}>{h}</th>;
                    })}
                  </tr>
                </thead>
                <tbody>
                  {invoices.map(function(inv) {
                    const ds = invSt(inv);
                    const canPay = ds !== 'paid' && inv.stripe_payment_link;
                    return (
                      <tr key={inv.id} style={{borderBottom:'1px solid #F0F0EC'}}>
                        <td style={{padding:'12px 16px',fontWeight:'600'}}>
                          <a href={'/invoice/' + inv.id} style={{textDecoration:'none',color:'#1d4ed8'}}>{dispNum(inv)}</a>
                        </td>
                        <td style={{padding:'12px 16px',color:'#374151'}}>{fmtD(inv.created_at)}</td>
                        <td style={{padding:'12px 16px',color:ds === 'overdue' ? '#ef4444' : '#374151',fontWeight:ds === 'overdue' ? '700' : undefined}}>{fmtD(inv.due_date)}</td>
                        <td style={{padding:'12px 16px',fontWeight:'600',color:'#0D0D0D'}}>{fm(inv.total)}</td>
                        <td style={{padding:'12px 16px'}}>
                          <span style={{background:ST_COLOR[ds]||'#6b7280',color:'white',padding:'3px 9px',borderRadius:'20px',fontSize:'11px',fontWeight:'700',whiteSpace:'nowrap'}}>
                            {ST_LABEL[ds]||ds}
                          </span>
                        </td>
                        <td style={{padding:'12px 16px',whiteSpace:'nowrap'}}>
                          {canPay
                            ? <a href={inv.stripe_payment_link} style={{color:'white',background:'#1A7A4A',padding:'5px 12px',borderRadius:'6px',fontWeight:'700',fontSize:'12px',textDecoration:'none'}}>Pay Now</a>
                            : <a href={'/invoice/' + inv.id} style={{color:'#1A7A4A',fontWeight:'600',fontSize:'12px',textDecoration:'none'}}>View</a>
                          }
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
}

export { OrderStatusPage, PrivacyPage, TermsPage, InvoicePage, ClientAccountPage, SmsOptInPage };
export default App;